import React, { useMemo, useState, useEffect } from 'react'
import ReactDOM from 'react-dom'
import { FaDownload, FaChevronRight } from 'react-icons/fa'
import {
  formatEur, formatRange, computePricePositions, computeOpportunities, computeSupplierExit, computeSupplierNegotiation, deduplicatedSavings, computeNullBaseline, generateActionPlan, generateExpandedStepData, VOLUME_EXP, VOLUME_RATIO_CAP,
} from './networkUtils'
import OppViz from './OppVisualizations'
import { exportOpportunitiesXlsx } from './oppExportXlsx'
import { generateDataPack } from './oppDataPack'
import AtlasPartDetailPanel from './AtlasPartDetailPanel'
import DivisionKPIBar from './DivisionKPIBar'

const API_BASE = process.env.REACT_APP_BE_MAIN_URL

/** Call backend to generate a negotiation brief PDF and trigger download */
// Static-demo hook: when present (set by the standalone demo build), brief/RFQ
// buttons open a pre-bundled PDF instead of calling the backend. No-op in the app.
function __demoArtifact(kind, opp, setLoading) {
  if (typeof window !== 'undefined' && window.__DEMO_ARTIFACT) {
    window.__DEMO_ARTIFACT(kind, opp); if (setLoading) setLoading(false); return true
  }
  return false
}

async function downloadPlaybookPDF(opp, setLoading) {
  if (__demoArtifact('playbook', opp, setLoading)) return
  setLoading(true)
  const token = sessionStorage.getItem('userToken')
  const d = opp.vizData || {}
  const items = (d.items || []).map(i => ({
    pn: i.pn, desc: i.desc || '', uc: i.uc, bm: i.bm, pp: i.pp,
    spend: i.spend, eau: i.eau || 0,
    file_id: i.file_id || (() => {
      const p = (opp.parts || []).find(pt => pt.pn === i.pn)
      return p?.file_id || null
    })(),
    material: i.material || '', method: i.method || '',
    // Full cohort (unsliced) — needed for complete consolidation-candidate discovery (playbook §5 Lever 1)
    cohort_entries: i.cohort_entries || [],
    cohort_name: i.cohort_name || '',
    cohort_size: i.cohort_size || 0,
    cohort_median_uc: i.cohort_median_uc || 0,
    cohort_median_eau: i.cohort_median_eau || 0,
    // Sole-source flag drives negotiation posture (Protect tier). Same logic as networkUtils.js sole-source detection.
    sole_source: (() => {
      const p = (opp.parts || []).find(pt => pt.pn === i.pn)
      return ((p?.suppliers) || []).filter(s => s.unit_cost > 0).length === 1
    })(),
  }))
  const goodItems = (d.goodItems || []).map(g => ({
    pn: g.pn, desc: g.desc || '', uc: g.uc, bm: g.bm, pp: g.pp,
    spend: g.spend, eau: g.eau || 0, file_id: g.file_id || null,
    material: g.material || '', method: g.method || '',
    cohort_entries: g.cohort_entries || [],
    cohort_name: g.cohort_name || '',
    cohort_size: g.cohort_size || 0,
    cohort_median_uc: g.cohort_median_uc || 0,
    cohort_median_eau: g.cohort_median_eau || 0,
  }))

  const body = {
    supplierName: d.supplierName || '',
    items,
    goodItems,
    strategies: opp.strategies || [],
    spendExposed: opp.spendExposed || 0,
    avgPP: opp.avgPP || 0,
    estSavings: opp.estSavings || [0, 0],
    negotiationTarget: opp._negotiationTarget || 1.1,
    volumeExp: items[0]?.vol_exp || VOLUME_EXP,
    volumeRatioCap: VOLUME_RATIO_CAP,
    // PN→file_id map for resolving cohort peer drawings
    pnFileMap: Object.fromEntries(
      (opp.parts || []).filter(p => p.file_id).map(p => [p.pn, p.file_id])
    ),
  }

  try {
    const res = await fetch(`${API_BASE}/ocr/network/playbook/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    })
    if (!res.ok) {
      const txt = await res.text()
      alert(`Negotiation Brief generation failed: ${txt}`)
      return
    }
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = res.headers.get('Content-Disposition')?.match(/filename="(.+)"/)?.[1] || 'Negotiation_Brief.pdf'
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  } catch (err) {
    alert(`Negotiation Brief generation error: ${err.message}`)
  } finally {
    setLoading(false)
  }
}

/** Call backend to generate a supplier-facing RFQ package and trigger download */
async function downloadRFQPackage(opp, setLoading, options = {}) {
  if (__demoArtifact('rfq-package', opp, setLoading)) return
  setLoading(true)
  const token = sessionStorage.getItem('userToken')
  const d = opp.vizData || {}
  const includeCarrot = !!options.includeCarrot
  // The carrot's candidate pool comes from the cohort_entries; send more of them
  // when the carrot is requested so the volume-opportunity list is rich.
  const itemEntryCap = includeCarrot ? 80 : 20
  const goodEntryCap = includeCarrot ? 40 : 10
  const items = (d.items || []).map(i => ({
    pn: i.pn, desc: i.desc || '', uc: i.uc, bm: i.bm, pp: i.pp,
    spend: i.spend, eau: i.eau || 0,
    file_id: i.file_id || (() => {
      const p = (opp.parts || []).find(pt => pt.pn === i.pn)
      return p?.file_id || null
    })(),
    material: i.material || '', method: i.method || '',
    cohort_entries: (i.cohort_entries || []).slice(0, itemEntryCap),
    cohort_name: i.cohort_name || '',
    cohort_size: i.cohort_size || 0,
  }))
  const goodItems = (d.goodItems || []).map(g => ({
    pn: g.pn, desc: g.desc || '', pp: g.pp, spend: g.spend, uc: g.uc, bm: g.bm,
    eau: g.eau || 0, file_id: g.file_id || null,
    material: g.material || '', method: g.method || '',
    cohort_entries: (g.cohort_entries || []).slice(0, goodEntryCap),
    cohort_name: g.cohort_name || '',
    cohort_size: g.cohort_size || 0,
  }))

  const body = {
    supplierName: d.supplierName || '',
    fromName: 'Thermo Fisher Scientific',
    items,
    goodItems,
    spendExposed: opp.spendExposed || 0,
    includeCarrot,
    pnFileMap: Object.fromEntries(
      (opp.parts || []).filter(p => p.file_id).map(p => [p.pn, p.file_id])
    ),
  }

  try {
    const res = await fetch(`${API_BASE}/ocr/network/rfq-package/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    })
    if (!res.ok) {
      const txt = await res.text()
      alert(`RFQ generation failed: ${txt}`)
      return
    }
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = res.headers.get('Content-Disposition')?.match(/filename="(.+)"/)?.[1] || 'RFQ_Package.pdf'
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  } catch (err) {
    alert(`RFQ generation error: ${err.message}`)
  } finally {
    setLoading(false)
  }
}

/** Slim, leakage-aware part payload shared by the consolidation brief + RFQ. */
function consolPartsPayload(opp) {
  return (opp.parts || []).map(p => ({
    pn: p.pn, description: p.description || '',
    method: p.method || '', material: p.material || '', size: p.size || '',
    tolerance_band: p.tolerance_band || '', complexity_tier: p.complexity_tier || '',
    tightest_mm: p.tightest_mm != null ? p.tightest_mm : null,
    axis_tier: p.axis_tier || '', gauge_band: p.gauge_band || '',
    file_id: p.file_id || null,
    suppliers: (p.suppliers || []).map(s => ({
      name: s.name, unit_cost: s.unit_cost, spend: s.spend, eau: s.eau,
    })),
  }))
}

function consolPnFileMap(opp) {
  return Object.fromEntries((opp.parts || []).filter(p => p.file_id).map(p => [p.pn, p.file_id]))
}

async function downloadBlob(res, fallbackName) {
  const blob = await res.blob()
  // Content-Disposition is hidden cross-origin (not CORS-exposed), so derive the
  // extension from the actual blob MIME type — a single bidder returns a PDF, many
  // return a ZIP. Falls back to the server filename when it IS readable.
  let name = res.headers.get('Content-Disposition')?.match(/filename="(.+)"/)?.[1]
  if (!name) {
    const ext = blob.type.includes('zip') ? 'zip' : blob.type.includes('pdf') ? 'pdf'
      : (fallbackName.split('.').pop() || 'pdf')
    name = fallbackName.replace(/\.[^.]+$/, '') + '.' + ext
  }
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = name
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

/** Call backend to generate the internal Consolidation Brief and trigger download. */
async function downloadConsolidationBrief(opp, setLoading) {
  if (__demoArtifact('consolidation-brief', opp, setLoading)) return
  setLoading(true)
  const token = sessionStorage.getItem('userToken')
  const v = opp.vizData || {}
  const body = {
    famLabel: v.famLabel || '',
    totalSpend: v.totalSpend || opp.spendExposed || 0,
    partCount: v.partCount || 0,
    supplierCount: v.supplierCount || 0,
    spread: v.spread || 0,
    best: v.best || {},
    worst: v.worst || {},
    suppliers: v.suppliers || [],
    partPrices: v.partPrices || [],
    parts: consolPartsPayload(opp),
    consolidation: opp.consolidation || v.consolidation || {},
    pnFileMap: consolPnFileMap(opp),
  }
  try {
    const res = await fetch(`${API_BASE}/ocr/network/consolidation-brief/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    })
    if (!res.ok) { alert(`Consolidation Brief generation failed: ${await res.text()}`); return }
    await downloadBlob(res, 'Consolidation_Brief.pdf')
  } catch (err) {
    alert(`Consolidation Brief generation error: ${err.message}`)
  } finally {
    setLoading(false)
  }
}

/** Call backend to generate the supplier-facing Consolidation RFQ (PDF or ZIP). */
async function downloadConsolidationRFQ(opp, setLoading, options = {}) {
  if (__demoArtifact('consolidation-rfq', opp, setLoading)) return
  setLoading(true)
  const token = sessionStorage.getItem('userToken')
  const v = opp.vizData || {}
  const body = {
    famLabel: v.famLabel || '',
    totalSpend: v.totalSpend || opp.spendExposed || 0,
    partCount: v.partCount || 0,
    supplierCount: v.supplierCount || 0,
    best: v.best || {},
    parts: consolPartsPayload(opp),
    consolidation: opp.consolidation || v.consolidation || {},
    recipients: options.recipients || [],
    revealAnchorPrice: !!options.revealAnchorPrice,
    fromName: 'Thermo Fisher Scientific',
    pnFileMap: consolPnFileMap(opp),
  }
  try {
    const res = await fetch(`${API_BASE}/ocr/network/consolidation-rfq/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    })
    if (!res.ok) { alert(`Consolidation RFQ generation failed: ${await res.text()}`); return }
    await downloadBlob(res, 'Consolidation_RFQ.zip')
  } catch (err) {
    alert(`Consolidation RFQ generation error: ${err.message}`)
  } finally {
    setLoading(false)
  }
}

const OPP_TYPES = {
  negotiation: { label: 'Negotiation', icon: '💰', color: '#dc2626', bg: '#fef2f2' },
  sole_source: { label: 'Sole-Source Risk', icon: '⚠️', color: '#d97706', bg: '#fffbeb' },
  part_family: { label: 'Part Family', icon: '🔗', color: '#6366f1', bg: '#eef2ff' },
  quick_win: { label: 'Quick Win', icon: '⚡', color: '#059669', bg: '#ecfdf5' },
  site_gap: { label: 'Site Price Gap', icon: '🌍', color: '#0891b2', bg: '#ecfeff' },
  supplier_exit: { label: 'Supplier Exit', icon: '🚪', color: '#7c3aed', bg: '#f5f3ff' },
  supplier_negotiation: { label: 'Negotiation Dossier', icon: '🤝', color: '#0369a1', bg: '#f0f9ff' },
}

const STEP_STYLE = `
.opp-action-step .step-data{background:#eef2ff;color:#3730a3;font-weight:600;padding:0 5px;border-radius:4px;white-space:nowrap}
.opp-action-step strong{color:#1f2937}
`

function Donut({ segments, count }) {
  const R = 60, CX = 80, CY = 80, C = 2 * Math.PI * R
  let cum = 0
  return (
    <svg viewBox="0 0 160 160" className="w-[110px] h-[110px] flex-shrink-0">
      {segments.map((seg, i) => {
        const dashLen = C * seg.pct / 100
        const el = (
          <circle key={i} cx={CX} cy={CY} r={R} fill="none" stroke={seg.color} strokeWidth="18"
            strokeDasharray={`${dashLen} ${C - dashLen}`} strokeDashoffset={-C * cum / 100}
            style={{ transition: 'stroke-dashoffset 0.5s' }} />
        )
        cum += seg.pct
        return el
      })}
      <text x={CX} y={CY - 4} textAnchor="middle" fontSize="22" fontWeight="700" fill="#1f2937">{count}</text>
      <text x={CX} y={CY + 14} textAnchor="middle" fontSize="9" fill="#94a3b8">opportunities</text>
    </svg>
  )
}

function Formula({ children }) {
  return (
    <div className="font-mono text-[11px] bg-gray-50 text-gray-700 border border-gray-200 rounded-md px-3 py-2 my-1.5 overflow-x-auto whitespace-pre leading-relaxed">{children}</div>
  )
}

function Section({ n, title, children }) {
  return (
    <div className="border-t border-gray-100 pt-4 mt-4 first:border-0 first:pt-0 first:mt-0">
      <div className="text-xs font-bold text-gray-800 mb-2">{n}. {title}</div>
      <div className="text-[11px] text-gray-600 leading-relaxed space-y-2">{children}</div>
    </div>
  )
}

const OPP_METHOD = [
  {
    icon: '💰', title: 'Negotiation', color: '#dc2626',
    trigger: 'A supplier’s line has Price Position > 1.3 and spend > €500. Lines are grouped by supplier; group must total > €500 overspend.',
    formula: 'overspend = spend × (1 − 1 / PP)',
    note: 'PP is the volume-normalized benchmark ratio (see §2). Grouping by supplier turns scattered lines into one negotiation with real leverage.',
  },
  {
    icon: '⚠️', title: 'Sole-Source Risk', color: '#d97706',
    trigger: 'Part has exactly 1 supplier and total spend > €2,000. Grouped by supplier; group must total > €5,000.',
    formula: 'savings ≈ 0.12 × total_spend',
    note: 'Without competitive tension, prices drift up. Qualifying a second source typically recovers 8–15%; we use a conservative 12%.',
  },
  {
    icon: '�', title: 'Part Family', color: '#6366f1',
    trigger: 'Engineering-similar parts (≥4 shared attributes: method, material, size, tolerance, complexity) that are either priced ≥30% apart OR fragmented across ≥4 suppliers. Combined savings must exceed €2,000.',
    formula: `Price alignment: expectedᵢ = best_UC × (best_EAU / EAUᵢ)^${VOLUME_EXP}, savings = Σ overpriced.spend × (1 − expectedᵢ / UCᵢ)\nConsolidation: savings ≈ 0.08 × family_spend\nFinal savings = max(price_alignment, consolidation)`,
    note: 'Combines three levers: similarity scoring (how similar are the parts really?), price alignment (why do similar parts cost different amounts?), and supplier consolidation (fragmented volume forfeits leverage). Parts with \u226570% similarity to the reference are strong candidates for price alignment; parts below 40% may have legitimate engineering differences.',
  },
  {
    icon: '⚡', title: 'Quick Win', color: '#059669',
    trigger: 'A part with ≥2 suppliers where one is expensive (PP > 1.5) and another is already cheap (PP < 1.0) — a qualified cheaper source exists.',
    formula: 'savings = worst.spend × (1 − best.PP / worst.PP)',
    note: 'No new qualification — just redirect the PO to the supplier already serving the part cheaper elsewhere.',
  },
]

function MethodologyPanel() {
  return (
    <details className="bg-white rounded-xl border border-gray-200 shadow-sm">
      <summary className="cursor-pointer px-5 py-3 text-sm font-semibold text-gray-700 select-none">How does this work?</summary>
      <div className="px-5 pb-5 space-y-6">

        {/* Visual pipeline overview */}
        <div className="bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-100 p-5">
          <div className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-3">Data Pipeline</div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex flex-col items-center px-3 py-2 bg-white rounded-lg border border-gray-200 shadow-sm min-w-[90px]">
              <div className="text-xl mb-1">📄</div>
              <div className="text-[10px] font-semibold text-gray-700">CAD Drawings</div>
              <div className="text-[9px] text-gray-400">Process, material, size</div>
            </div>
            <div className="text-gray-300 text-lg">→</div>
            <div className="flex flex-col items-center px-3 py-2 bg-white rounded-lg border border-gray-200 shadow-sm min-w-[90px]">
              <div className="text-xl mb-1">🤖</div>
              <div className="text-[10px] font-semibold text-gray-700">AI Extraction</div>
              <div className="text-[9px] text-gray-400">Tolerances, GD&T, dims</div>
            </div>
            <div className="text-gray-300 text-lg">→</div>
            <div className="flex flex-col items-center px-3 py-2 bg-white rounded-lg border border-gray-200 shadow-sm min-w-[90px]">
              <div className="text-xl mb-1">💳</div>
              <div className="text-[10px] font-semibold text-gray-700">Purchase Data</div>
              <div className="text-[9px] text-gray-400">Spend, unit cost, EAU</div>
            </div>
            <div className="text-gray-300 text-lg">→</div>
            <div className="flex flex-col items-center px-3 py-2 bg-white rounded-lg border border-gray-200 shadow-sm min-w-[90px]">
              <div className="text-xl mb-1">🔗</div>
              <div className="text-[10px] font-semibold text-gray-700">Join & Cohort</div>
              <div className="text-[9px] text-gray-400">Group similar parts</div>
            </div>
            <div className="text-gray-300 text-lg">→</div>
            <div className="flex flex-col items-center px-3 py-2 bg-[#ecfdf5] rounded-lg border border-green-200 shadow-sm min-w-[90px]">
              <div className="text-xl mb-1">💡</div>
              <div className="text-[10px] font-semibold text-green-800">Opportunities</div>
              <div className="text-[9px] text-green-600">Ranked & scored</div>
            </div>
          </div>
        </div>

        {/* Section 1: Data Matching */}
        <Section n="1" title="How purchase data matches to drawings">
          <p>
            Spend rows reference bare part numbers (e.g. <span className="font-mono text-[11px]">1098833</span>) while CAD drawings carry
            extensions and revision tails (<span className="font-mono text-[11px]">1098833-D-110-01.pdf</span>). Both sides are reduced
            to a <strong>canonical part-number key</strong> and joined tenant-wide, independent of folder.
          </p>
          <p className="mt-2">
            The deterministic key matches ~59% of drawings; the remaining are resolved by an AI fallback that records confident matches
            so the join stays fast. Multiple spend rows for one part (across sites/suppliers) all attach to that drawing.
          </p>
        </Section>

        {/* Section 2: Price Benchmarking */}
        <Section n="2" title="Price benchmarking (volume-normalized)">
          <p>
            Parts are grouped into <strong>sourcing cohorts</strong> — clusters sharing manufacturing characteristics.
            The more attributes shared, the more meaningful the comparison:
          </p>
          <div className="bg-gray-50 rounded-lg border border-gray-100 p-3 my-3">
            <div className="text-[10px] font-bold text-gray-600 uppercase mb-2">Cohort Attributes</div>
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <div className="flex gap-0.5"><div className="w-2 h-2 rounded-sm bg-green-500"/><div className="w-2 h-2 rounded-sm bg-green-500"/><div className="w-2 h-2 rounded-sm bg-green-500"/><div className="w-2 h-2 rounded-sm bg-green-500"/></div>
                <span className="text-[11px] text-gray-700"><strong>4+ attributes</strong> (method + material + size + tolerance) — most precise</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex gap-0.5"><div className="w-2 h-2 rounded-sm bg-green-500"/><div className="w-2 h-2 rounded-sm bg-green-500"/><div className="w-2 h-2 rounded-sm bg-green-500"/><div className="w-2 h-2 rounded-sm bg-gray-200"/></div>
                <span className="text-[11px] text-gray-700"><strong>3 attributes</strong> — solid, executable comparison</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex gap-0.5"><div className="w-2 h-2 rounded-sm bg-amber-500"/><div className="w-2 h-2 rounded-sm bg-amber-500"/><div className="w-2 h-2 rounded-sm bg-gray-200"/><div className="w-2 h-2 rounded-sm bg-gray-200"/></div>
                <span className="text-[11px] text-gray-700"><strong>2 attributes</strong> — directional; validate with quotes</span>
              </div>
            </div>
            <div className="text-[10px] text-gray-500 mt-2 italic">Parts with fewer than 2 known attributes are excluded from benchmarking.</div>
          </div>
          <p>Within each cohort (≥3 priced members) we compute a volume-adjusted benchmark:</p>
          <Formula>{`benchmark = cohort_median_UC × (cohort_median_EAU / part_EAU)^${VOLUME_EXP}
Price Position (PP) = unit_cost / benchmark
  PP 1.0 = at market    PP 1.5 = 50% above market`}</Formula>
          <p className="mt-2">
            The <span className="font-mono text-[11px]">^{VOLUME_EXP}</span> exponent is a volume price-break adjustment (~8% per volume doubling, capped at ±18%) —
            it prevents low-volume parts from being penalized for volume alone, without letting volume dominate the benchmark.
          </p>
        </Section>

        {/* Section 3: Savings Calculation */}
        <Section n="3" title="How savings are calculated">
          <p>
            Each opportunity type has its own savings formula. The <strong>Negotiation Target</strong> setting
            (adjustable in Opportunity Settings above) determines the price you aim to negotiate down to.
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-3">
            <div className="bg-gray-50 border border-gray-100 rounded-lg p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <span>💰</span>
                <span className="text-xs font-semibold text-red-600">Negotiation</span>
              </div>
              <Formula>{`savings = spend × (1 − target / PP)
Example: €10K spend at PP 1.5, target 1.1×
  = €10K × (1 − 1.1/1.5) = €2,667`}</Formula>
              <div className="text-[10px] text-gray-500 mt-1">Only parts with PP above the <em>Price Threshold</em> setting are flagged.</div>
            </div>
            <div className="bg-gray-50 border border-gray-100 rounded-lg p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <span>⚠️</span>
                <span className="text-xs font-semibold text-amber-600">Sole-Source Risk</span>
              </div>
              <Formula>{`savings = total_sole_spend × 12%`}</Formula>
              <div className="text-[10px] text-gray-500 mt-1">Assumes qualifying a second source yields 8–15% improvement through competitive pressure. 12% midpoint.</div>
            </div>
            <div className="bg-gray-50 border border-gray-100 rounded-lg p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <span>🔗</span>
                <span className="text-xs font-semibold text-indigo-600">Part Family</span>
              </div>
              <Formula>{`savings = Σ part_spend × (1 − best_UC / part_UC)
best_UC = cheapest in-family unit cost`}</Formula>
              <div className="text-[10px] text-gray-500 mt-1">Triggers when ≥4 similar parts are priced ≥30% apart OR fragmented across ≥4 suppliers.</div>
            </div>
            <div className="bg-gray-50 border border-gray-100 rounded-lg p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <span>⚡</span>
                <span className="text-xs font-semibold text-green-600">Quick Win</span>
              </div>
              <Formula>{`savings = spend × (1 − alt_PP / current_PP)
Where alt already qualified (PP < 1.0)`}</Formula>
              <div className="text-[10px] text-gray-500 mt-1">A part with ≥2 suppliers where one is expensive (PP &gt; 1.5) and another is cheap (PP &lt; 1.0).</div>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-3">
            <div className="text-[10px] font-bold text-amber-700 uppercase mb-1">Deduplication</div>
            <p className="text-[11px] text-amber-900">
              The same part+supplier can appear in multiple opportunity types. The headline "Potential Annual Savings" KPI
              only counts the <strong>highest savings claim</strong> for each part+supplier pair — never double-counts.
            </p>
          </div>
        </Section>

        {/* Section 4: Savings Display */}
        <Section n="4" title="Savings display & the Savings Estimate setting">
          <p>
            Raw modelled savings represent a theoretical maximum. Real negotiations rarely achieve 100%.
            The <strong>Savings Estimate</strong> slider applies a multiplier (default 65%) and shows a range:
          </p>
          <div className="bg-gray-50 rounded-lg p-3 my-3 text-[11px]">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="text-center"><div className="text-sm font-bold text-gray-700">€10,000</div><div className="text-[9px] text-gray-400">Raw model</div></div>
              <div className="text-gray-300 text-lg">→</div>
              <div className="text-center"><div className="text-sm font-bold text-gray-700">× 65%</div><div className="text-[9px] text-gray-400">Multiplier</div></div>
              <div className="text-gray-300 text-lg">→</div>
              <div className="text-center"><div className="text-sm font-bold text-green-600">€5K – €8K</div><div className="text-[9px] text-gray-400">Displayed range</div></div>
            </div>
          </div>
          <p>Adjusting up = more optimistic; adjusting down = safer for executive reporting.</p>
        </Section>

        {/* Section 5: Negotiation Target */}
        <Section n="5" title="Negotiation Target setting">
          <p>
            The <strong>Negotiation Target</strong> (default 1.1× = 10% premium over benchmark) defines the price level
            you aim to negotiate toward. It affects everything:
          </p>
          <ul className="list-disc pl-4 space-y-1 text-[11px] mt-2">
            <li><strong>Savings calculations</strong> — higher target = less savings per part (you accept a larger premium as "fair")</li>
            <li><strong>Action Plans</strong> — target prices, evidence text, and challenge scripts update dynamically</li>
            <li><strong>Negotiation Brief</strong> — per-part targets, anchor prices, and talking points all reflect the setting</li>
            <li><strong>Data Pack</strong> — printable data pack uses this target for all calculations</li>
            <li><strong>Chart tooltips</strong> — overspend and target price on hover reflect this setting</li>
          </ul>
          <div className="bg-gray-50 rounded-lg border border-gray-100 p-3 my-3">
            <div className="text-[10px] font-bold text-gray-600 uppercase mb-2">Practical guidance</div>
            <div className="space-y-1.5 text-[11px]">
              <div className="flex items-center gap-2"><span className="font-mono w-8 text-right">1.0×</span><span className="text-gray-600">Exact benchmark — aggressive, use with strong leverage</span></div>
              <div className="flex items-center gap-2"><span className="font-mono w-8 text-right font-semibold text-green-700">1.1×</span><span className="text-gray-600">Default — 10% premium for service/quality. Fair and defensible.</span></div>
              <div className="flex items-center gap-2"><span className="font-mono w-8 text-right">1.2×</span><span className="text-gray-600">Conservative — for strategic or hard-to-replace suppliers</span></div>
              <div className="flex items-center gap-2"><span className="font-mono w-8 text-right">1.5×+</span><span className="text-gray-600">Only flag extreme outliers — useful for data-quality investigation</span></div>
            </div>
          </div>
        </Section>

        {/* Section 6: Achievability */}
        <Section n="6" title="Achievability scoring">
          <p>Each opportunity receives an <strong>Achievability score</strong> (0–100) reflecting how actionable it is.</p>
          <div className="bg-gray-50 rounded-lg border border-gray-100 p-3 my-3">
            <div className="text-[10px] font-bold text-gray-600 uppercase mb-2">Scoring Factors</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px]">
              <div className="flex items-start gap-1.5"><span className="text-green-600 font-bold">+</span> <span><strong>Opportunity type</strong> — quick wins and part families score higher</span></div>
              <div className="flex items-start gap-1.5"><span className="text-green-600 font-bold">+</span> <span><strong>Cohort granularity</strong> — 4 attributes outscores 2</span></div>
              <div className="flex items-start gap-1.5"><span className="text-green-600 font-bold">+</span> <span><strong>Cohort depth</strong> — 10+ transactions outscores 3</span></div>
              <div className="flex items-start gap-1.5"><span className="text-green-600 font-bold">+</span> <span><strong>Line item count</strong> — 5+ parts give leverage</span></div>
              <div className="flex items-start gap-1.5"><span className="text-amber-600 font-bold">−</span> <span><strong>Outlier detection</strong> — PP &gt;3× flags potential data errors</span></div>
              <div className="flex items-start gap-1.5"><span className="text-red-600 font-bold">−</span> <span><strong>Execution complexity</strong> — new qualification reduces score</span></div>
            </div>
          </div>
          <div className="flex items-center gap-3 text-[11px] flex-wrap">
            <div className="flex items-center gap-1"><div className="flex gap-0.5">{[1,2,3,4].map(i=><div key={i} className="w-1.5 h-3 rounded-sm bg-green-500"/>)}</div><span><strong>High</strong></span></div>
            <div className="flex items-center gap-1"><div className="flex gap-0.5">{[1,2,3].map(i=><div key={i} className="w-1.5 h-3 rounded-sm bg-amber-500"/>)}<div className="w-1.5 h-3 rounded-sm bg-gray-200"/></div><span><strong>Medium</strong></span></div>
            <div className="flex items-center gap-1"><div className="flex gap-0.5">{[1,2].map(i=><div key={i} className="w-1.5 h-3 rounded-sm bg-red-500"/>)}<div className="w-1.5 h-3 rounded-sm bg-gray-200"/><div className="w-1.5 h-3 rounded-sm bg-gray-200"/></div><span><strong>Low</strong></span></div>
            <div className="flex items-center gap-1"><div className="flex gap-0.5">{[1].map(i=><div key={i} className="w-1.5 h-3 rounded-sm bg-gray-400"/>)}<div className="w-1.5 h-3 rounded-sm bg-gray-200"/><div className="w-1.5 h-3 rounded-sm bg-gray-200"/><div className="w-1.5 h-3 rounded-sm bg-gray-200"/></div><span><strong>Explore</strong></span></div>
          </div>
        </Section>

        {/* Section 7: Complexity Score */}
        <Section n="7" title="Manufacturing complexity score">
          <p>
            Each part receives a <strong>0–100 complexity score</strong> derived from 7 weighted axes of the CAD extraction.
            Higher complexity means harder to manufacture and fewer qualified suppliers — which affects achievability and cohort quality.
          </p>
          <div className="bg-gray-50 rounded-lg border border-gray-100 p-3 my-3">
            <div className="text-[10px] font-bold text-gray-600 uppercase mb-2">Scoring Axes (max 100 pts)</div>
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <div className="w-16 bg-gray-200 rounded-full h-2 overflow-hidden"><div className="bg-red-500 h-full rounded-full" style={{width:'100%'}}/></div>
                <span className="text-[11px] text-gray-700 flex-1"><strong>Feature density</strong> (20 pts) — holes, pockets, slots, undercuts, threads, bends, welds</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-16 bg-gray-200 rounded-full h-2 overflow-hidden"><div className="bg-red-500 h-full rounded-full" style={{width:'100%'}}/></div>
                <span className="text-[11px] text-gray-700 flex-1"><strong>Tolerance precision</strong> (20 pts) — tightest linear + GD&T tolerance band</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-16 bg-gray-200 rounded-full h-2 overflow-hidden"><div className="bg-amber-500 h-full rounded-full" style={{width:'75%'}}/></div>
                <span className="text-[11px] text-gray-700 flex-1"><strong>Axis / setup</strong> (15 pts) — 3/4/5-axis + number of setups required</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-16 bg-gray-200 rounded-full h-2 overflow-hidden"><div className="bg-amber-500 h-full rounded-full" style={{width:'50%'}}/></div>
                <span className="text-[11px] text-gray-700 flex-1"><strong>Material difficulty</strong> (10 pts) — Ti/Inco &gt; stainless &gt; steel &gt; aluminum &gt; plastic</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-16 bg-gray-200 rounded-full h-2 overflow-hidden"><div className="bg-amber-500 h-full rounded-full" style={{width:'50%'}}/></div>
                <span className="text-[11px] text-gray-700 flex-1"><strong>Specification density</strong> (10 pts) — dimension count + GD&T frames + surface finishes + notes</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-16 bg-gray-200 rounded-full h-2 overflow-hidden"><div className="bg-amber-500 h-full rounded-full" style={{width:'50%'}}/></div>
                <span className="text-[11px] text-gray-700 flex-1"><strong>Certification burden</strong> (10 pts) — NADCAP, ITAR, AS9100 and other cert requirements</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-16 bg-gray-200 rounded-full h-2 overflow-hidden"><div className="bg-green-500 h-full rounded-full" style={{width:'25%'}}/></div>
                <span className="text-[11px] text-gray-700 flex-1"><strong>Size / BTF</strong> (5 pts) — envelope class + buy-to-fly waste ratio</span>
              </div>
            </div>
            <div className="text-[10px] text-gray-500 mt-2 italic">+ Secondary processes bonus (up to +10 pts for heat treat, plating, NDT, etc.)</div>
          </div>
          <div className="flex items-center gap-4 text-[11px] flex-wrap mt-2">
            <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-green-100 border border-green-300"/><span><strong>Simple</strong> (0–25)</span></div>
            <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-amber-100 border border-amber-300"/><span><strong>Moderate</strong> (26–50)</span></div>
            <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-red-100 border border-red-300"/><span><strong>Complex</strong> (51–75)</span></div>
            <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-purple-100 border border-purple-300"/><span><strong>Very Complex</strong> (76–100)</span></div>
          </div>
          <p className="mt-2">
            Complexity feeds into <strong>cohort grouping</strong> (parts are benchmarked against peers of similar complexity),
            <strong>achievability scoring</strong> (complex parts are harder to re-source), and <strong>Part Family matching</strong>
            (the Complexity Tier attribute contributes 2 of 20 similarity points).
          </p>
        </Section>

        {/* Section 8: Data Quality */}
        <Section n="8" title="Data-quality rules">
          <ul className="list-disc pl-4 space-y-1.5 text-[11px]">
            <li><strong>Inforecord / price-reference rows excluded</strong> — zero-spend quoted list prices are not purchases.</li>
            <li><strong>EAU required for price opportunities.</strong> No volume = no PP = skipped by Negotiation/Quick-Win.</li>
            <li><strong>Outlier thresholds:</strong> PP &gt;3× → warning; &gt;5× → likely UoM mismatch; &gt;10× → data error.</li>
            <li><strong>Cross-site outlier removal</strong> — per-site prices &gt;8× the part median are excluded.</li>
            <li><strong>Spend-weighted ranking</strong> — high-value parts surface first.</li>
          </ul>
        </Section>

        {/* Section 9: Part Families */}
        <Section n="9" title="Part Family similarity analysis">
          <p>
            Each part is scored against the <strong>best-priced reference</strong> using a multi-attribute similarity function (max 20 pts):
          </p>
          <div className="bg-gray-50 rounded-lg border border-gray-100 p-3 my-3">
            <div className="grid grid-cols-2 gap-1.5 text-[11px]">
              <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded bg-green-500 text-[8px] text-white text-center font-bold leading-3">4</span> <span>Size band</span></div>
              <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded bg-green-500 text-[8px] text-white text-center font-bold leading-3">3</span> <span>Material family</span></div>
              <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded bg-green-500 text-[8px] text-white text-center font-bold leading-3">3</span> <span>Primary process</span></div>
              <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded bg-green-500 text-[8px] text-white text-center font-bold leading-3">3</span> <span>Dimensional proximity</span></div>
              <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded bg-amber-500 text-[8px] text-white text-center font-bold leading-3">2</span> <span>Tolerance band</span></div>
              <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded bg-amber-500 text-[8px] text-white text-center font-bold leading-3">2</span> <span>Complexity tier</span></div>
              <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded bg-amber-500 text-[8px] text-white text-center font-bold leading-3">2</span> <span>Envelope class</span></div>
              <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded bg-gray-400 text-[8px] text-white text-center font-bold leading-3">1</span> <span>Machining route</span></div>
            </div>
          </div>
          <div className="space-y-1 text-[11px]">
            <div className="flex items-center gap-2"><div className="w-2.5 h-2.5 rounded-full bg-green-600"/>{'≥'}70% — Strong match. Price differences are negotiable.</div>
            <div className="flex items-center gap-2"><div className="w-2.5 h-2.5 rounded-full bg-amber-500"/>40–69% — Moderate. Investigate before negotiating.</div>
            <div className="flex items-center gap-2"><div className="w-2.5 h-2.5 rounded-full bg-red-500"/>&lt;40% — Weak. Gap may reflect real engineering differences.</div>
          </div>
        </Section>

        {/* Section 10: Outputs */}
        <Section n="10" title="Negotiation Brief & Data Pack">
          <p>Each opportunity includes actionable outputs:</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
            <div className="bg-gray-50 border border-gray-100 rounded-lg p-3">
              <div className="text-[11px] font-semibold text-gray-700 mb-1">📕 Negotiation Brief <span className="text-[9px] text-gray-400 font-normal">(Negotiation only)</span></div>
              <ul className="list-disc pl-3 space-y-0.5 text-[10px] text-gray-600">
                <li>One-pager: asks, concession carrot, walk-away</li>
                <li>Posture segmentation (pressure / persuade / protect)</li>
                <li>Price distribution + vendor breakdown per part</li>
                <li>Objection → counter matrix with confidence tags</li>
                <li>Per-part evidence with drawing + talking points</li>
                <li>Decision-tree flowchart + CAD originals appended</li>
              </ul>
            </div>
            <div className="bg-gray-50 border border-gray-100 rounded-lg p-3">
              <div className="text-[11px] font-semibold text-gray-700 mb-1">📄 Data Pack <span className="text-[9px] text-gray-400 font-normal">(All types)</span></div>
              <ul className="list-disc pl-3 space-y-0.5 text-[10px] text-gray-600">
                <li>Line-item detail (UC, BM, PP, target, savings)</li>
                <li>Engineering equivalence (Part Family)</li>
                <li>Supplier landscape analysis</li>
                <li>Sensitivity analysis table</li>
                <li>Per-part cohort evidence</li>
                <li>Draft supplier email</li>
              </ul>
            </div>
          </div>
          <p className="mt-2 text-[11px] text-gray-500">
            Both outputs use the current <strong>Negotiation Target</strong> setting — change it and regenerated documents reflect the new target.
          </p>
        </Section>

        <Section n="11" title="Division Preference — Focused Optimization">
          <p>When you prefer a division, the engine optimizes the entire opportunity set for maximum ROI at that division's sites. Three adjustments are made — all transparent and reversible:</p>
          <div className="space-y-3 mt-3">
            <div className="bg-teal-50/50 border border-teal-100 rounded-lg p-3">
              <div className="text-[11px] font-semibold text-teal-800 mb-1">1. We look harder at your preferred division</div>
              <p className="text-[10px] text-teal-700">The price-position threshold drops from 1.3× to ~1.1× for parts at preferred-division sites. This surfaces opportunities that exist but are below the default detection floor.</p>
            </div>
            <div className="bg-teal-50/50 border border-teal-100 rounded-lg p-3">
              <div className="text-[11px] font-semibold text-teal-800 mb-1">2. We prefer routing volume to your division's suppliers</div>
              <p className="text-[10px] text-teal-700">In part-family consolidation, suppliers already serving the preferred division are favored as consolidation anchors when they're competitive.</p>
            </div>
            <div className="bg-teal-50/50 border border-teal-100 rounded-lg p-3">
              <div className="text-[11px] font-semibold text-teal-800 mb-1">3. We rank your division's wins higher</div>
              <p className="text-[10px] text-teal-700">Each opportunity gets a "division affinity" score (0–100%) based on the fraction of its spend at preferred-division sites. Sorting formula:</p>
              <code className="block mt-1 text-[10px] bg-white/60 rounded px-2 py-1 font-mono text-teal-900">effective_savings = raw_savings × (1 + 0.5 × affinity)</code>
              <p className="text-[10px] text-teal-700 mt-1">A 100% preferred-division opportunity ranks 50% higher than a 0% one with equal raw savings.</p>
            </div>
          </div>
          <div className="mt-3 bg-gray-50 border border-gray-100 rounded-lg p-3">
            <div className="text-[11px] font-semibold text-gray-700 mb-1">What doesn't change</div>
            <ul className="list-disc pl-3 space-y-0.5 text-[10px] text-gray-600">
              <li>Benchmark calculations remain cohort-based and objective</li>
              <li>Raw savings figures are never inflated</li>
              <li>Non-preferred division opportunities remain visible</li>
              <li>The Division KPI bar shows exact impact on every division</li>
            </ul>
          </div>
        </Section>
      </div>
    </details>
  )
}


const TUTORIAL_STEPS = [
  {
    title: 'Welcome to Savings Opportunities',
    icon: '👋',
    content: 'This page shows algorithmically-identified cost reduction opportunities across your purchased parts. Each opportunity is backed by data — prices, volumes, and engineering attributes extracted from your drawings and purchase history.',
    visual: (
      <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-3 mt-2">
        <div className="flex-1 text-center p-2"><div className="text-2xl mb-1">📄</div><div className="text-[10px] text-gray-500">CAD Drawings</div></div>
        <div className="text-gray-300 text-lg">+</div>
        <div className="flex-1 text-center p-2"><div className="text-2xl mb-1">💳</div><div className="text-[10px] text-gray-500">Purchase History</div></div>
        <div className="text-gray-300 text-lg">=</div>
        <div className="flex-1 text-center p-2"><div className="text-2xl mb-1">💡</div><div className="text-[10px] text-gray-500">Opportunities</div></div>
      </div>
    ),
  },
  {
    title: 'Opportunity Types',
    icon: '📊',
    content: 'There are 4 types of opportunities, each with a different mechanism:',
    visual: (
      <div className="grid grid-cols-1 gap-1.5 mt-2">
        <div className="flex items-center gap-2 bg-green-50 rounded px-2 py-1.5"><span>⚡</span><span className="text-[11px]"><strong>Quick Win</strong> — cheaper qualified supplier already exists. Just switch the PO.</span></div>
        <div className="flex items-center gap-2 bg-red-50 rounded px-2 py-1.5"><span>💰</span><span className="text-[11px]"><strong>Negotiation</strong> — supplier charges above the market benchmark. Negotiate with data.</span></div>
        <div className="flex items-center gap-2 bg-indigo-50 rounded px-2 py-1.5"><span>🔗</span><span className="text-[11px]"><strong>Part Family</strong> — engineering-similar parts with price spread or supplier fragmentation. Align pricing and consolidate.</span></div>
        <div className="flex items-center gap-2 bg-amber-50 rounded px-2 py-1.5"><span>⚠️</span><span className="text-[11px]"><strong>Sole-Source Risk</strong> — only one supplier. Qualify a second to enable competition.</span></div>
      </div>
    ),
  },
  {
    title: 'Achievability Rating',
    icon: '🎯',
    content: 'Not all opportunities are equally easy to capture. The Achievability column tells you how actionable each one is:',
    visual: (
      <div className="space-y-2 mt-2">
        <div className="flex items-center gap-3"><div className="flex gap-0.5">{[1,2,3,4].map(i=><div key={i} className="w-1.5 h-3 rounded-sm bg-green-500"/>)}</div><span className="text-[11px]"><strong>High</strong> — can execute immediately (e.g. redirect a PO to a cheaper site)</span></div>
        <div className="flex items-center gap-3"><div className="flex gap-0.5">{[1,2,3].map(i=><div key={i} className="w-1.5 h-3 rounded-sm bg-amber-500"/>)}<div className="w-1.5 h-3 rounded-sm bg-gray-200"/></div><span className="text-[11px]"><strong>Medium</strong> — requires a negotiation or quote request</span></div>
        <div className="flex items-center gap-3"><div className="flex gap-0.5">{[1,2].map(i=><div key={i} className="w-1.5 h-3 rounded-sm bg-red-500"/>)}{[3,4].map(i=><div key={i} className="w-1.5 h-3 rounded-sm bg-gray-200"/>)}</div><span className="text-[11px]"><strong>Low</strong> — coarse benchmark; validate with quotes before acting</span></div>
        <div className="flex items-center gap-3"><div className="flex gap-0.5"><div className="w-1.5 h-3 rounded-sm bg-gray-400"/>{[2,3,4].map(i=><div key={i} className="w-1.5 h-3 rounded-sm bg-gray-200"/>)}</div><span className="text-[11px]"><strong>Exploratory</strong> — directional signal; needs further investigation</span></div>
      </div>
    ),
  },
  {
    title: 'Reading an Opportunity',
    icon: '🔍',
    content: 'Click any row to expand it. Inside you will find:',
    visual: (
      <div className="space-y-1.5 mt-2 text-[11px]">
        <div className="flex items-start gap-2"><span className="text-green-600 font-bold">1.</span> A plain-English explanation of what was found and why it matters</div>
        <div className="flex items-start gap-2"><span className="text-green-600 font-bold">2.</span> A visual chart showing the price distribution or gap — hover any bar for details, click a part label to open its record</div>
        <div className="flex items-start gap-2"><span className="text-green-600 font-bold">3.</span> The affected parts with similarity scores (Part Families) — showing how comparable each part is to the reference</div>
        <div className="flex items-start gap-2"><span className="text-green-600 font-bold">4.</span> A step-by-step Action Plan with similarity intelligence — tells you which parts are safe to negotiate hard</div>
        <div className="flex items-start gap-2"><span className="text-green-600 font-bold">5.</span> A "Generate Data Pack" button that creates a print-ready brief with engineering equivalence analysis</div>
      </div>
    ),
  },
  {
    title: 'Taking Action',
    icon: '🚀',
    content: 'Start with High-Achievability opportunities at the top. For each one:',
    visual: (
      <div className="space-y-1.5 mt-2 text-[11px]">
        <div className="flex items-start gap-2 bg-green-50 rounded px-2 py-1.5"><span>✅</span> Expand the opportunity and review the Action Plan — it includes similarity-backed recommendations</div>
        <div className="flex items-start gap-2 bg-green-50 rounded px-2 py-1.5"><span>✅</span> Click any part label in the chart to inspect its engineering record and see similar parts</div>
        <div className="flex items-start gap-2 bg-green-50 rounded px-2 py-1.5"><span>✅</span> Click "Generate Data Pack" for a professional brief with engineering equivalence and price targets</div>
        <div className="flex items-start gap-2 bg-green-50 rounded px-2 py-1.5"><span>✅</span> Send the data pack to your supplier — it contains ready-to-use negotiation language</div>
        <div className="flex items-start gap-2 bg-green-50 rounded px-2 py-1.5"><span>✅</span> Use "Export Excel" to download all opportunities for your team</div>
        <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-[10px] text-amber-800">
          💡 Tip: Parts with similarity scores below 40% may have legitimate engineering differences — validate before demanding price alignment.
        </div>
      </div>
    ),
  },
]

function TutorialOverlay({ onClose }) {
  const [step, setStep] = useState(0)
  const s = TUTORIAL_STEPS[step]
  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-[520px] max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Progress */}
        <div className="flex gap-1 px-6 pt-5">
          {TUTORIAL_STEPS.map((_, i) => (
            <div key={i} className={`flex-1 h-1 rounded-full ${i <= step ? 'bg-[#1e5045]' : 'bg-gray-200'}`} />
          ))}
        </div>
        {/* Content */}
        <div className="px-6 py-5">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xl">{s.icon}</span>
            <h3 className="text-base font-bold text-gray-800">{s.title}</h3>
          </div>
          <p className="text-sm text-gray-600 leading-relaxed">{s.content}</p>
          {s.visual}
        </div>
        {/* Navigation */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50">
          <button onClick={onClose} className="text-xs text-gray-500 hover:text-gray-700">Skip tutorial</button>
          <div className="flex items-center gap-2">
            {step > 0 && (
              <button onClick={() => setStep(step - 1)} className="px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-100">Back</button>
            )}
            {step < TUTORIAL_STEPS.length - 1 ? (
              <button onClick={() => setStep(step + 1)} className="px-4 py-1.5 text-xs font-medium text-white bg-[#1e5045] rounded-lg hover:bg-[#0d5d57]">Next</button>
            ) : (
              <button onClick={onClose} className="px-4 py-1.5 text-xs font-medium text-white bg-[#1e5045] rounded-lg hover:bg-[#0d5d57]">Get started</button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}

function AchievabilityBadge({ achievability }) {
  const [showTip, setShowTip] = React.useState(false)
  const [tipStyle, setTipStyle] = React.useState({})
  const badgeRef = React.useRef(null)
  const tipRef = React.useRef(null)
  const hideTimer = React.useRef(null)

  if (!achievability) return null

  const colorMap = { green: 'bg-green-100 text-green-800 border-green-200', amber: 'bg-amber-100 text-amber-800 border-amber-200', red: 'bg-red-100 text-red-800 border-red-200', gray: 'bg-gray-100 text-gray-600 border-gray-200' }
  const barColor = { green: 'bg-green-500', amber: 'bg-amber-500', red: 'bg-red-500', gray: 'bg-gray-400' }

  const positionTip = () => {
    if (!badgeRef.current) return
    const rect = badgeRef.current.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom
    const above = spaceBelow < 200
    const left = Math.max(8, Math.min(rect.left + rect.width / 2 - 140, window.innerWidth - 300))
    if (above) {
      setTipStyle({ position: 'fixed', bottom: window.innerHeight - rect.top + 8, left, zIndex: 9999 })
    } else {
      setTipStyle({ position: 'fixed', top: rect.bottom + 8, left, zIndex: 9999 })
    }
  }

  const cancelHide = () => { if (hideTimer.current) { clearTimeout(hideTimer.current); hideTimer.current = null } }
  const scheduleHide = () => { cancelHide(); hideTimer.current = setTimeout(() => setShowTip(false), 150) }

  const handleMouseEnter = () => { cancelHide(); positionTip(); setShowTip(true) }
  const handleMouseLeave = () => { scheduleHide() }
  const handleTipEnter = () => { cancelHide() }
  const handleTipLeave = () => { scheduleHide() }

  return (
    <div className="relative inline-block">
      <div ref={badgeRef} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} className={`inline-flex items-center gap-1.5 text-[10px] font-medium px-2 py-0.5 rounded-full border cursor-help ${colorMap[achievability.color]}`}>
        <div className="flex gap-0.5">
          {[1,2,3,4].map(i => (
            <div key={i} className={`w-1 h-2.5 rounded-sm ${i <= Math.ceil(achievability.score / 25) ? barColor[achievability.color] : 'bg-gray-200'}`} />
          ))}
        </div>
        {achievability.label}
      </div>
      {showTip && ReactDOM.createPortal(
        <div
          ref={tipRef}
          onMouseEnter={handleTipEnter}
          onMouseLeave={handleTipLeave}
          style={tipStyle}
          className="w-[280px] bg-white text-gray-700 rounded-lg shadow-lg border border-gray-200 px-4 py-3"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-gray-800">Achievability</span>
            <span className={`text-xs font-bold ${achievability.color === 'green' ? 'text-green-700' : achievability.color === 'amber' ? 'text-amber-700' : achievability.color === 'red' ? 'text-red-700' : 'text-gray-500'}`}>{achievability.score}/100</span>
          </div>
          <p className="text-[11px] text-gray-600 leading-relaxed">{achievability.tip}</p>
          {achievability.warnings.length > 0 && (
            <div className="mt-2 pt-2 border-t border-gray-100 space-y-1">
              {achievability.warnings.map((w, i) => (
                <div key={i} className="flex items-start gap-1.5 text-[11px] text-amber-700">
                  <span className="shrink-0 mt-0.5">&#9888;</span>
                  <span>{w}</span>
                </div>
              ))}
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  )
}

function buildPPClipboardText(s, part) {
  const pp = s.price_position
  if (pp == null || !s.benchmark) return ''
  const pct = Math.abs(Math.round((pp - 1) * 100))
  const direction = pp > 1 ? 'above' : 'below'
  const eau = parseInt(s.eau) || 0
  const verdict = pp > 1.5 ? 'Above expected — negotiation target' : pp < 0.7 ? 'Well below expected — good deal' : 'Within normal range'
  let header = ''
  if (part) {
    header = `Part: ${part.pn || '—'}${part.description ? ' — ' + part.description : ''}\n`
  }
  let formula = ''
  if (s.cohort_median_uc && s.cohort_median_eau && eau > 0) {
    formula = `\nFormula: Expected = Cohort Median UC × (Cohort Median EAU / Part EAU)^${VOLUME_EXP}\n       = €${s.cohort_median_uc.toFixed(2)} × (${s.cohort_median_eau.toLocaleString()} / ${eau.toLocaleString()})^${VOLUME_EXP}\n       = €${s.benchmark.toFixed(2)}`
  }
  let text = `${header}Supplier: ${s.name}\nActual: €${Number(s.unit_cost).toFixed(2)}\nExpected: €${s.benchmark.toFixed(2)}${eau > 0 ? ` (EAU ${eau.toLocaleString()})` : ''}\nPosition: ${pct}% ${direction} benchmark\n${verdict}${formula}`
  const entries = (s.cohort_entries || []).sort((a, b) => b.unit_cost - a.unit_cost)
  if (entries.length > 0) {
    const cohortLabel = (s.cohort_name || 'Unknown').replace(/_/g, ' ').replace(/\|/g, ', ')
    text += `\n\nCohort: ${cohortLabel} (${s.cohort_size || entries.length} transactions from parts with same process family/method/material/size)\nMedian UC: €${s.cohort_median_uc?.toFixed(2) || '?'}, Median EAU: ${s.cohort_median_eau?.toLocaleString() || '?'}`
    text += `\nPart #\tDescription\tSupplier\tUC\tEAU`
    entries.forEach(e => {
      text += `\n${e.pn || '—'}\t${e.description || '—'}\t${e.supplier}\t€${e.unit_cost.toFixed(2)}\t${e.eau > 0 ? Math.round(e.eau).toLocaleString() : '—'}`
    })
  }
  return text
}

function PPTooltip({ s, part }) {
  const pp = s.price_position
  if (pp == null && !s.benchmark) {
    return <div className="text-[11px] text-gray-500">No price position — EAU data missing, cannot compute benchmark.</div>
  }
  if (pp == null) return null

  const pct = Math.abs(Math.round((pp - 1) * 100))
  const direction = pp > 1 ? 'above' : 'below'
  const eau = parseInt(s.eau) || 0
  const verdictColor = pp > 1.5 ? 'text-red-600' : pp < 0.7 ? 'text-green-700' : 'text-gray-600'
  const verdict = pp > 1.5 ? '⚠ Above expected — negotiation target' : pp < 0.7 ? '✓ Well below expected — good deal' : '○ Within normal range'

  const entries = (s.cohort_entries || []).sort((a, b) => b.unit_cost - a.unit_cost).slice(0, 10)
  const hasFormula = s.cohort_median_uc && s.cohort_median_eau && eau > 0
  const cohortLabel = (s.cohort_name || 'Unknown').replace(/_/g, ' ').replace(/\|/g, ', ')

  return (
    <div>
      {part && (
        <div className="text-[10px] text-gray-500 mb-1 truncate">
          <span className="font-mono font-medium text-gray-700">{part.pn}</span>
          {part.description && <span className="ml-1.5">{part.description}</span>}
        </div>
      )}
      <div className={`flex ${entries.length > 0 ? 'gap-4' : ''}`}>
        {/* Left: Price position details */}
        <div className="shrink-0">
          <div className="font-semibold text-[11px] text-gray-900 truncate mb-1">{s.name}</div>
          <table className="text-[10px]">
            <tbody>
              <tr><td className="text-gray-500 pr-2 py-0.5">Actual</td><td className="font-mono text-gray-900">€{Number(s.unit_cost).toFixed(2)}</td></tr>
              <tr><td className="text-gray-500 pr-2 py-0.5">Expected</td><td className="font-mono text-gray-900">€{s.benchmark.toFixed(2)}{eau > 0 ? <span className="text-gray-400 ml-1">(EAU {eau.toLocaleString()})</span> : ''}</td></tr>
              <tr><td className="text-gray-500 pr-2 py-0.5">Position</td><td className={`font-semibold ${verdictColor}`}>{pct}% {direction}</td></tr>
            </tbody>
          </table>
          <div className={`text-[10px] font-medium mt-1 ${verdictColor}`}>{verdict}</div>
          {hasFormula && (
            <div className="mt-1.5 pt-1.5 border-t border-gray-100 text-[9px] text-gray-500 font-mono leading-relaxed">
              <div>Expected = Median UC × (Median EAU / Part EAU)<sup>{VOLUME_EXP}</sup></div>
              <div className="text-gray-400">= €{s.cohort_median_uc.toFixed(2)} × ({s.cohort_median_eau.toLocaleString()} / {eau.toLocaleString()})<sup>{VOLUME_EXP}</sup> = €{s.benchmark.toFixed(2)}</div>
            </div>
          )}
        </div>
        {/* Right: Cohort table */}
        {entries.length > 0 && (
          <div className="border-l border-gray-200 pl-3 min-w-0 overflow-x-auto">
            <div className="text-[9px] font-semibold text-gray-500 uppercase tracking-wide mb-1">
              {cohortLabel} ({s.cohort_size || entries.length})
              {s.cohort_median_uc && <span className="font-normal normal-case ml-1">· median €{s.cohort_median_uc.toFixed(2)}</span>}
            </div>
            <table className="w-full text-[10px]">
              <thead>
                <tr className="text-gray-400 border-b border-gray-100">
                  <th className="text-left font-medium pb-0.5 pr-2">Part</th>
                  <th className="text-left font-medium pb-0.5 pr-2">Supplier</th>
                  <th className="text-right font-medium pb-0.5 pr-2">UC</th>
                  <th className="text-right font-medium pb-0.5">EAU</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e, i) => (
                  <tr key={i} className={e.supplier === s.name ? 'text-gray-900 font-medium' : 'text-gray-600'}>
                    <td className="pr-2 py-0.5 truncate max-w-[140px] font-mono" title={`${e.pn} — ${e.description || ''}`}>{e.pn?.slice(0, 14) || '—'}{e.description ? <span className="font-sans text-gray-400 ml-1 text-[9px]">{e.description.slice(0, 12)}</span> : ''}</td>
                    <td className="pr-2 py-0.5 truncate max-w-[120px]">{e.supplier.slice(0, 18)}{e.supplier.length > 18 ? '…' : ''}</td>
                    <td className="text-right pr-2 py-0.5 font-mono whitespace-nowrap">€{e.unit_cost.toFixed(2)}</td>
                    <td className="text-right py-0.5 font-mono whitespace-nowrap">{e.eau > 0 ? Math.round(e.eau).toLocaleString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(s.cohort_entries || []).length > 10 && (
              <div className="text-[9px] text-gray-400 mt-0.5">+{s.cohort_entries.length - 10} more</div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function SupplierChip({ s, multi, part }) {
  const [copied, setCopied] = React.useState(false)
  const [showTip, setShowTip] = React.useState(false)
  const [tipStyle, setTipStyle] = React.useState({})
  const chipRef = React.useRef(null)
  const tipRef = React.useRef(null)
  const hideTimer = React.useRef(null)
  const pp = s.price_position
  const ppColor = pp == null ? 'text-gray-400' : pp > 1.5 ? 'text-red-600' : pp < 0.7 ? 'text-green-600' : 'text-gray-500'
  const uc = s.unit_cost > 0 ? `€${Number(s.unit_cost).toFixed(2)}` : '—'
  const hasTip = pp != null || !s.benchmark

  const positionTip = () => {
    if (!chipRef.current) return
    const rect = chipRef.current.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom
    const above = spaceBelow < 350
    const tipW = 680 // max-w of tooltip
    const left = Math.max(8, Math.min(rect.left + rect.width / 2 - tipW / 2, window.innerWidth - tipW - 12))
    if (above) {
      setTipStyle({ position: 'fixed', bottom: window.innerHeight - rect.top + 8, left, maxWidth: window.innerWidth - 24, zIndex: 9999 })
    } else {
      setTipStyle({ position: 'fixed', top: rect.bottom + 8, left, maxWidth: window.innerWidth - 24, zIndex: 9999 })
    }
  }

  const cancelHide = () => { if (hideTimer.current) { clearTimeout(hideTimer.current); hideTimer.current = null } }
  const scheduleHide = () => { cancelHide(); hideTimer.current = setTimeout(() => setShowTip(false), 150) }

  const handleMouseEnter = () => {
    cancelHide()
    positionTip()
    setShowTip(true)
  }

  const handleMouseLeave = () => { scheduleHide() }
  const handleTipEnter = () => { cancelHide() }
  const handleTipLeave = () => { scheduleHide() }

  const handleCopy = (e) => {
    e.stopPropagation()
    const text = buildPPClipboardText(s, part)
    if (text) {
      navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    }
  }

  return (
    <span ref={chipRef} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded border text-[10px] ${multi ? 'border-gray-300 bg-white' : 'border-gray-200 bg-gray-50'} relative`}>
      <span className="text-gray-700">{s.name.slice(0, 24)}{s.name.length > 24 ? '…' : ''}</span>
      <span className="font-mono text-gray-500">{uc}</span>
      {pp != null && (
        <span className={`font-semibold cursor-help ${ppColor}`}>
          {pp.toFixed(2)}{pp > 1.5 ? ' ⚠️' : pp < 0.7 ? ' ✓' : ''}
        </span>
      )}
      {hasTip && showTip && ReactDOM.createPortal(
        <div
          ref={tipRef}
          onClick={handleCopy}
          onMouseEnter={handleTipEnter}
          onMouseLeave={handleTipLeave}
          style={tipStyle}
          className="min-w-[420px] max-w-[680px] w-max bg-white text-gray-700 rounded-lg shadow-lg border border-gray-200 px-3 py-2.5 cursor-pointer hover:border-gray-300 transition-colors overflow-x-auto"
        >
          <PPTooltip s={s} part={part} />
          <div className="mt-2 pt-1.5 border-t border-gray-100 text-[9px] text-gray-400 text-center">
            {copied ? '✓ Copied to clipboard' : 'Click to copy'}
          </div>
        </div>,
        document.body
      )}
    </span>
  )
}

function ActionPlan({ opp, onPartClick }) {
  const steps = generateActionPlan(opp)
  const expansions = React.useMemo(() => generateExpandedStepData(opp), [opp])
  const [expanded, setExpanded] = React.useState({})
  const [generatingPDF, setGeneratingPDF] = React.useState(false)
  const [generatingRFQ, setGeneratingRFQ] = React.useState(false)
  const [rfqIncludeCarrot, setRfqIncludeCarrot] = React.useState(false)
  // Part-family consolidation RFQ: anchor/recipient confirm step + price-withhold toggle
  const [generatingConsolRFQ, setGeneratingConsolRFQ] = React.useState(false)
  const [showRfqConfig, setShowRfqConfig] = React.useState(false)
  const [revealAnchorPrice, setRevealAnchorPrice] = React.useState(false)
  const recipientOptions = React.useMemo(() => {
    const tv = opp.consolidation?.targetVendors || {}
    return [...new Map(
      [tv.anchor, ...(tv.candidates || []), ...(tv.netNew || [])].filter(Boolean).map(c => [c.name, c])
    ).values()]
  }, [opp.consolidation])
  const [selectedRecipients, setSelectedRecipients] = React.useState(() => {
    const tv = opp.consolidation?.targetVendors || {}
    return new Set([tv.anchor?.name, ...(tv.netNew || []).map(n => n.name)].filter(Boolean))
  })
  const toggleRecipient = (name) => setSelectedRecipients(prev => {
    const n = new Set(prev); n.has(name) ? n.delete(name) : n.add(name); return n
  })
  // Build part number → file lookup for clickable PNs (includes overpriced + competitive items)
  const partMap = React.useMemo(() => {
    const m = {}
    ;(opp.parts || []).forEach(p => {
      if (p.file_id || p.filename) m[p.pn] = { fileId: p.file_id || null, filename: p.filename || '' }
    })
    ;(opp.vizData?.goodItems || []).forEach(g => {
      if (!m[g.pn] && (g.file_id || g.filename)) m[g.pn] = { fileId: g.file_id || null, filename: g.filename || '' }
    })
    return m
  }, [opp.parts, opp.vizData])
  if (!steps.length) return null

  const toggle = (i) => setExpanded(prev => ({ ...prev, [i]: !prev[i] }))

  return (
    <div className="mt-4 bg-[#f0fdf4] border border-green-200 rounded-lg p-4">
      <div className="flex items-center gap-2 text-sm font-semibold text-gray-800 mb-2">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" /><rect x="9" y="3" width="6" height="4" rx="1" /><path d="M9 12l2 2 4-4" /></svg>
        Action Plan
        {expansions.length > 0 && <span className="text-[10px] text-gray-400 font-normal ml-2">Click steps to expand</span>}
      </div>
      <ol className="space-y-1 list-none pl-0">
        {steps.map((s, i) => {
          const exp = expansions[i] || null
          const isOpen = expanded[i]
          return (
            <li key={i} className="border border-gray-100 rounded-md overflow-hidden">
              <div
                className={`flex items-start gap-2 px-3 py-2 ${exp ? 'cursor-pointer hover:bg-green-50' : ''} transition-colors`}
                onClick={() => exp && toggle(i)}
              >
                <span className="text-[10px] text-gray-400 font-mono mt-0.5 w-4 flex-shrink-0">{i + 1}.</span>
                {exp && (
                  <svg className={`w-3 h-3 mt-0.5 flex-shrink-0 text-gray-400 transition-transform ${isOpen ? 'rotate-90' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6" /></svg>
                )}
                <span className="opp-action-step text-xs text-gray-700 leading-relaxed flex-1" dangerouslySetInnerHTML={{ __html: s }} />
              </div>
              {isOpen && exp && <StepExpansion data={exp} partMap={partMap} onPartClick={onPartClick} />}
            </li>
          )
        })}
      </ol>
      <div className="mt-3 pt-3 border-t border-green-200 flex items-center gap-2 flex-wrap">
        <span className="text-xs">{'\uD83C\uDFAF'}</span>
        <span className="text-xs text-gray-500">Target outcome:</span>
        <span className="text-sm font-bold text-green-700">{formatEur(opp.estSavings * (opp.evidenceTier === 'A' ? 1.0 : opp.evidenceTier === 'C' ? 0.2 : 0.5))}</span>
        <span className="text-[11px] text-gray-400">annual savings (capture-rate adjusted)</span>
      </div>
      <button
        disabled={generatingPDF}
        onClick={(e) => {
          e.stopPropagation()
          if (opp.type === 'negotiation') {
            downloadPlaybookPDF(opp, setGeneratingPDF)
          } else if (opp.type === 'part_family') {
            downloadConsolidationBrief(opp, setGeneratingPDF)
          } else {
            generateDataPack(opp)
          }
        }}
        className={`mt-3 inline-flex items-center gap-2 px-4 py-2 text-white text-xs font-medium rounded-lg transition-colors ${
          generatingPDF ? 'bg-gray-400 cursor-wait' : 'bg-[#1e5045] hover:bg-[#0d5d57]'
        }`}
      >
        {generatingPDF ? (
          <svg className="animate-spin" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" strokeDasharray="31.4 31.4" strokeLinecap="round" /></svg>
        ) : (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
        )}
        {generatingPDF ? 'Generating Brief…' : (opp.type === 'negotiation' ? 'Download Negotiation Brief' : opp.type === 'part_family' ? 'Download Consolidation Brief' : 'Generate Data Pack')}
      </button>
      {opp.type === 'negotiation' && (
        <button
          disabled={generatingRFQ}
          onClick={(e) => {
            e.stopPropagation()
            downloadRFQPackage(opp, setGeneratingRFQ, { includeCarrot: rfqIncludeCarrot })
          }}
          className={`mt-3 ml-2 inline-flex items-center gap-2 px-4 py-2 text-xs font-medium rounded-lg border transition-colors ${
            generatingRFQ ? 'bg-gray-100 text-gray-400 border-gray-300 cursor-wait' : 'bg-white text-[#1e5045] border-[#1e5045] hover:bg-[#f6f4ed]'
          }`}
        >
          {generatingRFQ ? (
            <svg className="animate-spin" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" strokeDasharray="31.4 31.4" strokeLinecap="round" /></svg>
          ) : (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" /></svg>
          )}
          {generatingRFQ ? 'Generating RFQ…' : 'Generate RFQ Package'}
        </button>
      )}
      {opp.type === 'negotiation' && (
        <label
          className="mt-3 inline-flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none"
          title="Adds a 'Volume Opportunity' section listing additional parts (with component details and original drawings) this supplier could win by pricing competitively"
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="checkbox"
            checked={rfqIncludeCarrot}
            disabled={generatingRFQ}
            onChange={(e) => setRfqIncludeCarrot(e.target.checked)}
            className="w-3.5 h-3.5 accent-[#1e5045]"
          />
          Include consolidation carrot
        </label>
      )}
      {opp.type === 'part_family' && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); setShowRfqConfig(s => !s) }}
            className="mt-3 ml-2 inline-flex items-center gap-2 px-4 py-2 text-xs font-medium rounded-lg border bg-white text-[#1e5045] border-[#1e5045] hover:bg-[#f6f4ed] transition-colors"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /></svg>
            {showRfqConfig ? 'Hide RFQ setup' : 'Generate Consolidation RFQ…'}
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); generateDataPack(opp) }}
            className="mt-3 ml-2 inline-flex items-center gap-2 px-4 py-2 text-xs font-medium rounded-lg border bg-white text-gray-600 border-gray-300 hover:bg-gray-50 transition-colors"
            title="Client-side analyst worksheet (engineering equivalence + price targets)"
          >
            Analyst Data Pack
          </button>
          {showRfqConfig && (
            <div className="mt-3 p-3 bg-white border border-gray-200 rounded-lg" onClick={(e) => e.stopPropagation()}>
              <div className="text-xs font-semibold text-gray-700 mb-1">Run a competitive event</div>
              <div className="text-[11px] text-gray-500 mb-2">
                Recommended anchor: <span className="font-semibold text-[#1e5045]">{opp.consolidation?.targetVendors?.anchor?.name || '—'}</span>
                {opp.consolidation?.targetVendors?.anchor && <span className="text-gray-400"> · score {opp.consolidation.targetVendors.anchor.score}/100</span>}.
                Pick which bidders receive the package (3–5 is the sourcing norm).
              </div>
              {recipientOptions.length === 0 ? (
                <div className="text-[11px] text-gray-400 italic">No candidate vendors found in the analytics for this family.</div>
              ) : (
                <div className="flex flex-col gap-1 mb-2">
                  {recipientOptions.map(c => {
                    const tag = c.type === 'net_new' ? 'net-new' : (c.suppliesBest ? 'anchor' : 'incumbent')
                    const tagColor = tag === 'anchor' ? 'bg-green-100 text-green-700' : tag === 'net-new' ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-600'
                    return (
                      <label key={c.name} className="inline-flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer select-none">
                        <input type="checkbox" checked={selectedRecipients.has(c.name)} disabled={generatingConsolRFQ}
                          onChange={() => toggleRecipient(c.name)} className="w-3.5 h-3.5 accent-[#1e5045]" />
                        <span className="font-medium">{c.name}</span>
                        <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${tagColor}`}>{tag}</span>
                        {c.score != null && <span className="text-[10px] text-gray-400">· {c.score}/100</span>}
                      </label>
                    )
                  })}
                </div>
              )}
              <label className="inline-flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none mb-2"
                title="Off (recommended for cold bidders): the best-in-family price stays the internal anchor. On: include market context (friendly-incumbent invites only).">
                <input type="checkbox" checked={revealAnchorPrice} disabled={generatingConsolRFQ}
                  onChange={(e) => setRevealAnchorPrice(e.target.checked)} className="w-3.5 h-3.5 accent-[#1e5045]" />
                Reveal best-in-family price to bidders
              </label>
              <div>
                <button
                  disabled={generatingConsolRFQ || selectedRecipients.size === 0}
                  onClick={() => downloadConsolidationRFQ(opp, setGeneratingConsolRFQ, {
                    recipients: [...selectedRecipients], revealAnchorPrice,
                  })}
                  className={`inline-flex items-center gap-2 px-4 py-2 text-white text-xs font-medium rounded-lg transition-colors ${
                    generatingConsolRFQ || selectedRecipients.size === 0 ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#1e5045] hover:bg-[#0d5d57]'
                  }`}
                >
                  {generatingConsolRFQ ? (
                    <svg className="animate-spin" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" strokeDasharray="31.4 31.4" strokeLinecap="round" /></svg>
                  ) : (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                  )}
                  {generatingConsolRFQ ? 'Generating…' : `Generate RFQ (${selectedRecipients.size} bidder${selectedRecipients.size === 1 ? '' : 's'})`}
                </button>
                {selectedRecipients.size > 1 && <span className="ml-2 text-[10px] text-gray-400">Delivered as a ZIP, one PDF per bidder.</span>}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

function StepExpansion({ data, partMap, onPartClick }) {
  // Render a cell value — if it matches a part number in partMap, make it clickable
  const renderCell = (cell, ci, headers) => {
    const hdr = (headers[ci] || '').toLowerCase()
    const isPnCol = hdr.includes('part')
    if (isPnCol && typeof cell === 'string' && partMap && partMap[cell] && onPartClick) {
      return (
        <td key={ci} className="px-2 py-1 font-mono whitespace-nowrap">
          <button onClick={(e) => { e.stopPropagation(); onPartClick(partMap[cell]) }} className="text-[#1e5045] hover:underline cursor-pointer font-semibold">{cell}</button>
        </td>
      )
    }
    return <td key={ci} className="px-2 py-1 text-gray-700 font-mono whitespace-nowrap">{cell}</td>
  }

  return (
    <div className="px-4 pb-3 pt-1 bg-white border-t border-gray-100">
      {/* Bullet points */}
      {data.bullets && data.bullets.length > 0 && (
        <ul className="text-[11px] text-gray-600 space-y-1 mb-2 list-disc pl-4">
          {data.bullets.map((b, i) => <li key={i}>{b}</li>)}
        </ul>
      )}
      {/* Tables */}
      {data.tables && data.tables.map((t, ti) => (
        <div key={ti} className="mb-2">
          <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">{t.title}</div>
          <div className="overflow-x-auto">
            <table className="w-full text-[10px] border border-gray-200 rounded">
              <thead>
                <tr className="bg-gray-50 text-gray-500 border-b border-gray-200">
                  {t.headers.map((h, hi) => <th key={hi} className="px-2 py-1 text-left font-medium">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {t.rows.map((row, ri) => (
                  <tr key={ri} className="border-b border-gray-50 hover:bg-gray-50">
                    {row.map((cell, ci) => renderCell(cell, ci, t.headers))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
      {/* Email draft */}
      {data.emailDraft && (
        <div className="mt-2">
          <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Draft Email</div>
          <pre className="text-[10px] text-gray-700 bg-gray-50 border border-gray-200 rounded-md p-3 whitespace-pre-wrap font-sans leading-relaxed max-h-[300px] overflow-y-auto">{data.emailDraft}</pre>
          <button
            onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(data.emailDraft) }}
            className="mt-1 text-[10px] text-indigo-600 hover:text-indigo-800 font-medium cursor-pointer"
          >Copy to clipboard</button>
        </div>
      )}
    </div>
  )
}

function ExpandedDetail({ opp, onPartClick, enrichedParts, params }) {
  // Sort parts to match the chart order: part_family sorts by lowest priced UC ascending.
  // (Hook must run unconditionally — the supplier_exit branch returns below it.)
  const sortedParts = React.useMemo(() => {
    if (opp.type === 'part_family') {
      // Match vizData.partPrices order (by unit cost ascending)
      const priceMap = {}
      ;(opp.vizData?.partPrices || []).forEach((p, i) => { priceMap[p.pn] = i })
      return [...opp.parts].sort((a, b) => {
        const ai = priceMap[a.pn] ?? 9999
        const bi = priceMap[b.pn] ?? 9999
        return ai - bi
      })
    }
    return opp.parts
  }, [opp])

  // Supplier exit has its own interactive renderer (veto + redistribution map).
  if (opp.type === 'supplier_exit') {
    return <SupplierExitDetail opp={opp} onPartClick={onPartClick} enrichedParts={enrichedParts} params={params} />
  }

  return (
    <div className="px-4 py-4 bg-gray-50 border-t border-gray-200">
      <div className="text-xs text-gray-600 leading-relaxed mb-3 bg-white border border-gray-200 rounded-md px-3 py-2">{opp.detail}</div>
      <div className="bg-white border border-gray-200 rounded-lg p-3 mb-3"><OppViz opp={opp} onPartClick={onPartClick} /></div>
      <div className="overflow-x-auto">
        <table className="w-full text-[11px]">
          <thead>
            <tr className="text-left text-gray-400 border-b border-gray-200">
              <th className="py-1.5 pr-3 font-medium">Part #</th>
              <th className="py-1.5 pr-3 font-medium">Description</th>
              <th className="py-1.5 pr-3 font-medium">Suppliers</th>
              <th className="py-1.5 font-medium text-right">Spend</th>
            </tr>
          </thead>
          <tbody>
            {sortedParts.slice(0, 25).map(p => (
              <tr key={p.pn} className="border-b border-gray-100 align-top">
                <td className="py-1.5 pr-3 font-mono text-gray-700">
                  <button onClick={() => onPartClick({ fileId: p.file_id || null, filename: p.filename })} className="text-[#1e5045] hover:underline cursor-pointer text-left">{p.pn}</button>
                </td>
                <td className="py-1.5 pr-3 text-gray-500">
                  <button onClick={() => onPartClick({ fileId: p.file_id || null, filename: p.filename })} className="text-gray-500 hover:text-[#1e5045] hover:underline cursor-pointer text-left">{p.description || '—'}</button>
                </td>
                <td className="py-1.5 pr-3"><div className="flex flex-wrap gap-1">{(p.suppliers || []).map((s, i) => <SupplierChip key={i} s={s} multi={p.suppliers.length > 1} part={p} />)}</div></td>
                <td className="py-1.5 text-right font-mono text-gray-700">{formatEur(p.total_spend)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {sortedParts.length > 25 && <div className="text-[10px] text-gray-400 mt-1">+{sortedParts.length - 25} more parts (see Excel export)</div>}
      </div>
      <ActionPlan opp={opp} onPartClick={onPartClick} />
    </div>
  )
}

// ─── Supplier Exit: interactive redistribution view with veto / fallback ──────
function SupplierExitDetail({ opp, onPartClick, enrichedParts, params }) {
  const supplierName = opp.vizData?.supplierName
  const [vetoed, setVetoed] = useState(() => new Set(opp.vizData?.vetoedReceivers || []))
  const [openReceiver, setOpenReceiver] = useState(null)
  const [openPart, setOpenPart] = useState(null)
  const [capState, setCapState] = useState({ loading: false, results: null, error: null })

  // Pure re-compute: veto only changes receiver eligibility, parts cascade to next-best.
  const plan = useMemo(() => {
    if (!enrichedParts || !supplierName) return opp
    return computeSupplierExit(enrichedParts, supplierName, { ...params, vetoedReceivers: [...vetoed] })
  }, [enrichedParts, supplierName, params, vetoed, opp])

  const v = plan.vizData || {}
  // Group redistributable parts by their assigned (primary) receiver.
  const byReceiver = useMemo(() => {
    const m = {}
    ;(v.redistributable || []).forEach(rp => {
      const k = rp.primary?.name
      if (k) (m[k] = m[k] || []).push(rp)
    })
    return m
  }, [v])

  // Group sole-sourced (stuck + vetoed-out) parts by cohort — the units we send
  // to SourceOptima AI for capability profiling.
  const stuckByCohort = useMemo(() => {
    const m = {}
    ;[...(v.stuck || []), ...(v.vetoedOut || [])].forEach(sp => {
      const k = sp.cohort || 'uncategorized'
      ;(m[k] = m[k] || []).push(sp)
    })
    return m
  }, [v])

  const analyzeSourcing = async () => {
    setCapState({ loading: true, results: null, error: null })
    try {
      // Send only the part requirement signature — the model generates abstract,
      // matchable vendor profiles and never sees (or names) real suppliers.
      const units = Object.entries(stuckByCohort).map(([cohort, sps]) => {
        const first = sps[0] || {}
        return {
          key: cohort,
          cohort,
          parts: sps.map(sp => ({ filename: sp.filename, pn: sp.pn, desc: sp.desc })),
          signature: {
            method: first.method, material: first.material, size: first.size,
            tolerance_band: first.tolerance_band, complexity_tier: first.complexity_tier,
            axis_tier: first.axis_tier,
          },
        }
      })
      const token = sessionStorage.getItem('userToken')
      const API_BASE = process.env.REACT_APP_BE_MAIN_URL || ''
      const res = await fetch(`${API_BASE}/ocr/network/supplier-capability/`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ units, supplierName }),
      })
      const data = await res.json()
      if (!res.ok || data.demo) throw new Error(data.error || 'Capability analysis is only available with a connected backend.')
      setCapState({ loading: false, results: data.results || {}, error: null })
    } catch (e) {
      setCapState({ loading: false, results: null, error: e.message || 'Analysis failed' })
    }
  }
  const toggleVeto = (name) => setVetoed(prev => {
    const next = new Set(prev)
    next.has(name) ? next.delete(name) : next.add(name)
    return next
  })

  const Pill = ({ label, value, sub, color }) => (
    <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 flex-1 min-w-[130px]">
      <div className="text-base font-bold" style={{ color: color || '#1f2937' }}>{value}</div>
      <div className="text-[10px] font-semibold text-gray-500 uppercase mt-0.5">{label}</div>
      {sub && <div className="text-[10px] text-gray-400">{sub}</div>}
    </div>
  )

  return (
    <div className="px-4 py-4 bg-gray-50 border-t border-gray-200">
      <div className="text-xs text-gray-600 leading-relaxed mb-3 bg-white border border-gray-200 rounded-md px-3 py-2">{plan.detail}</div>

      {/* Headline pills */}
      <div className="flex gap-2 flex-wrap mb-3">
        <Pill label="Total exposure" value={formatEur(v.totalExposure)} sub={`${v.partCount} parts · ${v.familyCount} families`} />
        <Pill label="Redistributable" value={formatEur(v.redistributableSpend)} sub={`${(v.redistributable || []).length} parts → ${(v.receivers || []).length} suppliers`} color="#7c3aed" />
        <Pill label="Sole-source (stuck)" value={formatEur(v.stuckSpend)} sub={`${(v.stuck || []).length} parts need net-new qual.`} color="#d97706" />
        <Pill label="Savings range" value={`${formatEur(v.savingsFloor)} – ${formatEur(v.savingsCeiling)}`} sub="floor (defensible) → ceiling" color="#059669" />
      </div>

      {/* Active vetoes */}
      {vetoed.size > 0 && (
        <div className="flex items-center gap-2 flex-wrap mb-3 text-[11px]">
          <span className="text-gray-500 font-medium">Vetoed receivers:</span>
          {[...vetoed].map(n => (
            <button key={n} onClick={() => toggleVeto(n)} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200 hover:bg-red-100">
              {n} <span className="font-bold">×</span>
            </button>
          ))}
          <button onClick={() => setVetoed(new Set())} className="text-gray-400 hover:text-gray-600 underline">reset all</button>
        </div>
      )}

      {/* Redistribution map — expand a receiver to see the exact parts it absorbs */}
      <div className="bg-white border border-gray-200 rounded-lg p-3 mb-3">
        <div className="text-[11px] font-semibold text-gray-700 mb-2">Redistribution map — where {supplierName}'s parts move <span className="text-gray-400 font-normal">· click a supplier to see its parts</span></div>
        <table className="w-full text-[11px]">
          <thead>
            <tr className="text-left text-gray-400 border-b border-gray-200">
              <th className="py-1.5 pr-3 font-medium">Receiving supplier</th>
              <th className="py-1.5 pr-3 font-medium text-right">Parts</th>
              <th className="py-1.5 pr-3 font-medium text-right">Spend absorbed</th>
              <th className="py-1.5 pr-3 font-medium text-right">Families</th>
              <th className="py-1.5 font-medium text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {(v.receivers || []).map(r => {
              const isOpen = openReceiver === r.name
              const parts = byReceiver[r.name] || []
              return (
                <React.Fragment key={r.name}>
                  <tr className="border-b border-gray-100 cursor-pointer hover:bg-gray-50" onClick={() => { setOpenReceiver(isOpen ? null : r.name); setOpenPart(null) }}>
                    <td className="py-1.5 pr-3 text-gray-700 font-medium">
                      <span className="inline-flex items-center gap-1.5">
                        <FaChevronRight size={8} className={`text-gray-400 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                        {r.name}
                      </span>
                    </td>
                    <td className="py-1.5 pr-3 text-right font-mono text-gray-600">{r.partsAbsorbed}</td>
                    <td className="py-1.5 pr-3 text-right font-mono text-gray-700">{formatEur(r.spendAbsorbed)}</td>
                    <td className="py-1.5 pr-3 text-right font-mono text-gray-500">{r.familyCount}</td>
                    <td className="py-1.5 text-right">
                      <button onClick={(e) => { e.stopPropagation(); toggleVeto(r.name) }} className="px-2 py-0.5 rounded border border-red-200 text-red-600 hover:bg-red-50 text-[10px] font-medium">Veto</button>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-gray-50/70"><td colSpan={5} className="px-3 py-2">
                      <table className="w-full text-[11px]">
                        <thead>
                          <tr className="text-left text-gray-400 border-b border-gray-200">
                            <th className="py-1 pr-3 font-medium">Part #</th>
                            <th className="py-1 pr-3 font-medium">Description</th>
                            <th className="py-1 pr-3 font-medium text-right">Spend</th>
                            <th className="py-1 pr-3 font-medium text-right">Now → projected</th>
                            <th className="py-1 font-medium">Fallback</th>
                          </tr>
                        </thead>
                        <tbody>
                          {parts.map(rp => (
                            <React.Fragment key={rp.pn}>
                              <tr className="border-b border-gray-100 cursor-pointer hover:bg-white" onClick={() => setOpenPart(openPart === rp.pn ? null : rp.pn)}>
                                <td className="py-1 pr-3 font-mono">
                                  <button onClick={(e) => { e.stopPropagation(); onPartClick({ fileId: rp.file_id || null, filename: rp.filename }) }} className="text-[#1e5045] hover:underline">{rp.pn}</button>
                                </td>
                                <td className="py-1 pr-3 text-gray-500 truncate max-w-[220px]">{rp.desc || '—'}</td>
                                <td className="py-1 pr-3 text-right font-mono text-gray-700">{formatEur(rp.spend)}</td>
                                <td className="py-1 pr-3 text-right font-mono text-gray-600">€{(rp.uc || 0).toFixed(2)} → €{(rp.primary?.projUc || 0).toFixed(2)}</td>
                                <td className="py-1 text-gray-400">{rp.fallback?.name || '—'}</td>
                              </tr>
                              {openPart === rp.pn && (
                                <tr className="bg-white"><td colSpan={5} className="px-3 py-2">
                                  <div className="text-[10px] text-gray-500 mb-1">Ranked alternatives in cohort <span className="font-mono">{rp.cohort}</span> (current price €{(rp.uc || 0).toFixed(2)}):</div>
                                  <div className="flex flex-wrap gap-1.5">
                                    {(rp.alternatives || []).map((alt, i) => (
                                      <span key={alt.name} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] ${alt.vetoed ? 'bg-red-50 border-red-200 text-red-400 line-through' : alt.name === rp.primary?.name ? 'bg-violet-50 border-violet-200 text-violet-700 font-medium' : 'bg-white border-gray-200 text-gray-600'}`}>
                                        {i + 1}. {alt.name} · €{(alt.projUc || 0).toFixed(2)}{alt.typical_pp != null ? ` · ${alt.typical_pp.toFixed(2)}×` : ''}
                                      </span>
                                    ))}
                                  </div>
                                </td></tr>
                              )}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    </td></tr>
                  )}
                </React.Fragment>
              )
            })}
            {(v.receivers || []).length === 0 && <tr><td colSpan={5} className="py-2 text-gray-400 text-center">No eligible receivers — all vetoed.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Stuck / vetoed-out parts — continuity risk + AI sourcing analysis */}
      {((v.stuck || []).length > 0 || (v.vetoedOut || []).length > 0) && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
            <div className="text-[11px] font-semibold text-amber-800">
              Continuity risk — {(v.stuck || []).length} sole-sourced{(v.vetoedOut || []).length ? ` + ${v.vetoedOut.length} vetoed-out` : ''} parts ({formatEur((v.stuckSpend || 0) + (v.vetoedOutSpend || 0))}) across {Object.keys(stuckByCohort).length} cohorts
            </div>
            <button
              onClick={analyzeSourcing}
              disabled={capState.loading}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium transition-colors ${capState.loading ? 'bg-violet-300 text-white cursor-wait' : 'bg-violet-600 text-white hover:bg-violet-700'}`}
            >
              {capState.loading
                ? <><svg className="animate-spin" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" strokeDasharray="31.4 31.4" strokeLinecap="round" /></svg> Analyzing sourcing options…</>
                : <>✨ Find sourcing options — SourceOptima AI</>}
            </button>
          </div>
          <div className="text-[10px] text-amber-700 mb-2">These parts have no acceptable in-base receiver — a full exit requires developing a current supplier or qualifying a net-new one. SourceOptima AI profiles the capabilities each part demands and generates matchable vendor profiles (an ideal target and a developable near-miss) you can match against your supply base and rolodex.</div>
          {capState.error && <div className="text-[10px] text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1 mb-2">{capState.error}</div>}

          {/* Per-cohort: stuck parts + (when analyzed) capability readout */}
          {Object.entries(stuckByCohort).map(([cohort, sps]) => (
            <div key={cohort} className="bg-white border border-amber-200 rounded-lg p-2.5 mb-2">
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <span className="text-[11px] font-semibold text-gray-700 font-mono">{cohort}</span>
                <span className="text-[10px] text-gray-400">{sps.length} parts · {formatEur(sps.reduce((s, p) => s + p.spend, 0))}</span>
              </div>
              <table className="w-full text-[11px] mb-1">
                <tbody>
                  {sps.slice().sort((a, b) => b.spend - a.spend).slice(0, 6).map(sp => (
                    <tr key={sp.pn} className="border-b border-gray-100">
                      <td className="py-1 pr-3 font-mono">
                        <button onClick={() => onPartClick({ fileId: sp.file_id || null, filename: sp.filename })} className="text-[#1e5045] hover:underline">{sp.pn}</button>
                      </td>
                      <td className="py-1 pr-3 text-gray-500 truncate max-w-[260px]">{sp.desc || '—'}</td>
                      <td className="py-1 text-right font-mono text-gray-700">{formatEur(sp.spend)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {sps.length > 6 && <div className="text-[10px] text-gray-400 mb-1">+{sps.length - 6} more parts in this cohort</div>}
              {capState.results && capState.results[cohort] && <CapabilityReadout analysis={capState.results[cohort]} />}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// Renders one cohort's SourceOptima AI output: the required-capability profile and
// abstract, matchable target vendor profiles. No real supplier is named or scored —
// matching these profiles to the base / rolodex is a separate, later step.
function CapabilityReadout({ analysis }) {
  if (analysis.error) return <div className="text-[10px] text-red-500 mt-1">Analysis unavailable for this cohort.</div>
  const reqs = analysis.required_capabilities || []
  const reqById = Object.fromEntries(reqs.map(r => [r.id, r]))
  const profiles = analysis.target_vendor_profiles || []
  const critColor = { must_have: '#7c3aed', important: '#0891b2', nice_to_have: '#94a3b8' }
  const Cap = ({ id, gap }) => {
    const r = reqById[id]
    return (
      <span title={r?.detail || ''} className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium border ${gap ? 'bg-amber-50 border-amber-200 text-amber-600' : 'bg-violet-50 border-violet-200 text-violet-700'}`}>
        <b>{id}</b>{r ? ` ${r.label}` : ''}{gap ? ' (develop)' : ''}
      </span>
    )
  }
  const profMeta = {
    ideal: { label: 'Ideal match', bg: 'bg-violet-50', border: 'border-violet-200', text: 'text-violet-700' },
    developable_near_miss: { label: 'Developable near-miss', bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700' },
  }
  return (
    <div className="mt-2 border-t border-dashed border-violet-200 pt-2">
      <div className="flex items-center gap-1.5 mb-1.5">
        <span className="text-[10px] font-semibold text-violet-700 uppercase tracking-wide">✨ SourceOptima AI — sourcing profile</span>
        {analysis._cached && <span className="text-[9px] text-gray-400">(cached)</span>}
      </div>
      {analysis.cohort_summary && <div className="text-[11px] text-gray-700 mb-2 italic">{analysis.cohort_summary}</div>}

      {/* Required capabilities */}
      <div className="mb-2">
        <div className="text-[10px] font-semibold text-gray-500 uppercase mb-1">Required capabilities</div>
        <div className="space-y-1">
          {reqs.map(r => (
            <div key={r.id} className="flex items-start gap-1.5 text-[10px]">
              <span className="inline-flex items-center justify-center w-4 h-4 rounded-full text-white font-bold flex-shrink-0 mt-0.5" style={{ background: critColor[r.criticality] || '#94a3b8' }}>{r.id}</span>
              <span>
                <b className="text-gray-800">{r.label}</b> <span className="text-gray-400">[{(r.criticality || '').replace('_', ' ')}]</span> — <span className="text-gray-600">{r.detail}</span>
                {(r.match_signals || []).length > 0 && (
                  <span className="ml-1">{r.match_signals.map(t => <span key={t} className="inline-block ml-1 text-[8px] bg-gray-100 text-gray-500 rounded px-1 py-0.5 font-mono">{t}</span>)}</span>
                )}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Matchable target vendor profiles (no real names) */}
      <div>
        <div className="text-[10px] font-semibold text-gray-500 uppercase mb-1">Target vendor profiles <span className="text-gray-400 normal-case font-normal">— match against your base &amp; rolodex</span></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {profiles.map((pf, i) => {
            const m = profMeta[pf.profile_type] || { label: pf.profile_type, bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-600' }
            return (
              <div key={i} className={`${m.bg} border ${m.border} rounded p-2`}>
                <div className="flex items-center gap-1.5 mb-1">
                  <span className={`text-[9px] font-semibold uppercase ${m.text}`}>{m.label}</span>
                </div>
                <div className="text-[11px] font-semibold text-gray-800">{pf.archetype}</div>
                {pf.profile_summary && <div className="text-[10px] text-gray-600 mt-0.5">{pf.profile_summary}</div>}
                {(pf.capability_coverage || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {(pf.capability_coverage || []).map(id => <Cap key={'c' + id} id={id} />)}
                    {(pf.development_gaps || []).map(id => <Cap key={'d' + id} id={id} gap />)}
                  </div>
                )}
                {(pf.certifications || []).length > 0 && <div className="text-[10px] text-gray-500 mt-1.5"><b>Certifications:</b> {pf.certifications.join(', ')}</div>}
                {(pf.equipment || []).length > 0 && <div className="text-[10px] text-gray-500 mt-0.5"><b>Equipment:</b> {pf.equipment.join(', ')}</div>}
                {(pf.search_keywords || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5 items-center">
                    <span className="text-[10px] text-gray-500">Match:</span>
                    {pf.search_keywords.map(k => <span key={k} className="text-[9px] bg-white border border-gray-200 rounded px-1.5 py-0.5 text-gray-600">{k}</span>)}
                  </div>
                )}
                {pf.region_hint && <div className="text-[10px] text-gray-500 mt-1"><b>Region:</b> {pf.region_hint}</div>}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

const DEFAULT_PARAMS = {
  // Tier-aware capture rates (replace flat savingsMultiplier)
  captureA: 1.0,   // Tier A (observed same-part evidence) — proven, no haircut needed
  captureB: 0.5,   // Tier B (model-inferred) — requires negotiation, 50% capture realistic
  captureC: 0.2,   // Tier C (assumptions) — requires a program, 10-20% capture
  priceThreshold: 1.3,
  minSize: 2000,
  outlierThreshold: 3,
  similarMinAttrs: 4,
  negotiationTarget: 1.1,
  dispersionMax: 2.5,
  minCohortSize: 5,
  // Part-family consolidation savings bridge — cited, defensible constants.
  adminCostPerSupplier: 5000,
  toolingAmortPerSupplier: 3000,
  dualSourceThreshold: 150000,
}

function ConfigSlider({ label, help, value, onChange, min, max, step, format }) {
  const pct = ((value - min) / (max - min)) * 100
  return (
    <div className="flex-1 min-w-[200px]">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[11px] font-medium text-gray-700">{label}</span>
        <span className="text-[11px] font-mono text-gray-500">{format(value)}</span>
      </div>
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={e => onChange(parseFloat(e.target.value))}
        className="w-full h-1.5 rounded-full appearance-none cursor-pointer bg-gray-200 accent-[#1e5045]"
        style={{ background: `linear-gradient(to right, #1e5045 0%, #1e5045 ${pct}%, #e5e7eb ${pct}%, #e5e7eb 100%)` }}
      />
      <div className="flex justify-between text-[10px] text-gray-500 mt-0.5">
        <span>{format(min)}</span>
        <span>{format(max)}</span>
      </div>
      {help && <div className="text-[10px] text-gray-500 mt-1">{help}</div>}
    </div>
  )
}

function ConfigPanel({ params, setParams, parts = [] }) {
  const isDefault = JSON.stringify(params) === JSON.stringify(DEFAULT_PARAMS)
  const attrCoverage = React.useMemo(() => {
    const axes = [
      { key: 'method', label: 'Method' },
      { key: 'material', label: 'Material' },
      { key: 'size', label: 'Size' },
      { key: 'tolerance_band', label: 'Tolerance' },
      { key: 'complexity_tier', label: 'Complexity' },
    ]
    const isUnknown = v => !v || ['unknown','unassigned','n/a','none'].includes(String(v).toLowerCase().trim()) || String(v).toLowerCase().trim().endsWith('unspec')
    return axes.map(a => ({ ...a, count: parts.filter(p => !isUnknown(p[a.key])).length }))
  }, [parts])
  return (
    <details className="bg-white rounded-xl border border-gray-200 shadow-sm">
      <summary className="cursor-pointer px-5 py-3 text-sm font-semibold text-gray-700 select-none flex items-center gap-2">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 15a3 3 0 100-6 3 3 0 000 6z"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09a1.65 1.65 0 00-1-1.51 1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09a1.65 1.65 0 001.51-1 1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06a1.65 1.65 0 001.82.33h.09a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82v.09a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>
        Opportunity Settings
        {!isDefault && <span className="ml-2 text-[9px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-normal">Modified</span>}
      </summary>
      <div className="px-5 pb-5 pt-2">
        <div className="flex flex-wrap gap-x-8 gap-y-5">
          <ConfigSlider
            label="Capture Rate A (Observed)"
            help="Expected realization rate for Tier A (same-part evidence — PO redirect)"
            value={params.captureA}
            onChange={v => setParams(p => ({ ...p, captureA: v }))}
            min={0.5} max={1.0} step={0.05}
            format={v => `${Math.round(v * 100)}%`}
          />
          <ConfigSlider
            label="Capture Rate B (Inferred)"
            help="Expected realization rate for Tier B (negotiation required)"
            value={params.captureB}
            onChange={v => setParams(p => ({ ...p, captureB: v }))}
            min={0.2} max={0.8} step={0.05}
            format={v => `${Math.round(v * 100)}%`}
          />
          <ConfigSlider
            label="Capture Rate C (Assumptions)"
            help="Expected realization rate for Tier C (consolidation program required)"
            value={params.captureC}
            onChange={v => setParams(p => ({ ...p, captureC: v }))}
            min={0.05} max={0.5} step={0.05}
            format={v => `${Math.round(v * 100)}%`}
          />
          <ConfigSlider
            label="Negotiation Target"
            help="Price level to negotiate toward (1.0 = benchmark, 1.1 = 10% premium)"
            value={params.negotiationTarget}
            onChange={v => setParams(p => ({ ...p, negotiationTarget: v }))}
            min={1.0} max={2.5} step={0.1}
            format={v => `${v.toFixed(1)}× benchmark`}
          />
          <ConfigSlider
            label="Price Threshold"
            help="Min % above benchmark to flag as overpriced"
            value={params.priceThreshold}
            onChange={v => setParams(p => ({ ...p, priceThreshold: v }))}
            min={1.1} max={1.8} step={0.05}
            format={v => `>${Math.round((v - 1) * 100)}% above`}
          />
          <div>
            <ConfigSlider
              label="Similar-Part Matching"
              help="Shared attributes needed for cross-PN comparison"
              value={params.similarMinAttrs}
              onChange={v => setParams(p => ({ ...p, similarMinAttrs: v }))}
              min={2} max={5} step={1}
              format={v => `${v} attributes`}
            />
            {parts.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-gray-400">
                {attrCoverage.map(a => (
                  <span key={a.key}>{a.label} <span className="font-mono text-gray-500">({a.count})</span></span>
                ))}
              </div>
            )}
          </div>
          <ConfigSlider
            label="Data Quality — Outlier Threshold"
            help="Price ratio above which to flag as likely error"
            value={params.outlierThreshold}
            onChange={v => setParams(p => ({ ...p, outlierThreshold: v }))}
            min={2} max={10} step={0.5}
            format={v => `>${v.toFixed(1)}x = warning`}
          />
          <ConfigSlider
            label="Cohort Spread Limit (P75/P25)"
            help="Negotiation opportunities are only surfaced when the cohort's price spread is below this ratio — wider cohorts aren't defensible as 'comparable parts'."
            value={params.dispersionMax}
            onChange={v => setParams(p => ({ ...p, dispersionMax: v }))}
            min={1.5} max={5} step={0.25}
            format={v => `≤${v.toFixed(2)}x`}
          />
          <ConfigSlider
            label="Min. Cohort Depth"
            help="Minimum number of comparable priced parts required before a benchmark is negotiation-grade."
            value={params.minCohortSize}
            onChange={v => setParams(p => ({ ...p, minCohortSize: v }))}
            min={3} max={10} step={1}
            format={v => `≥${v} parts`}
          />
          <ConfigSlider
            label="Minimum Opportunity Size"
            value={params.minSize}
            onChange={v => setParams(p => ({ ...p, minSize: v }))}
            min={500} max={50000} step={500}
            format={v => formatEur(v)}
          />
        </div>
        <div className="mt-5 pt-4 border-t border-gray-100">
          <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-3">Part-Family Consolidation</div>
          <div className="flex flex-wrap gap-x-8 gap-y-5">
            <ConfigSlider
              label="Admin Cost / Supplier"
              help="Fully-loaded annual cost to manage one active supplier (POs, qualification, audits, AP). Feeds the consolidation savings bridge — cited in the Data Pack."
              value={params.adminCostPerSupplier}
              onChange={v => setParams(p => ({ ...p, adminCostPerSupplier: v }))}
              min={0} max={15000} step={500}
              format={v => `${formatEur(v)}/yr`}
            />
            <ConfigSlider
              label="Tooling Amort. / Supplier"
              help="Duplicated tooling amortization avoided per eliminated supplier — applied only to tooling-intensive processes (casting, molding, stamping, etc.)."
              value={params.toolingAmortPerSupplier}
              onChange={v => setParams(p => ({ ...p, toolingAmortPerSupplier: v }))}
              min={0} max={10000} step={500}
              format={v => `${formatEur(v)}/yr`}
            />
            <ConfigSlider
              label="Dual-Source Threshold"
              help="Annual family spend above which the risk register recommends retaining a qualified second source for continuity."
              value={params.dualSourceThreshold}
              onChange={v => setParams(p => ({ ...p, dualSourceThreshold: v }))}
              min={25000} max={500000} step={25000}
              format={v => formatEur(v)}
            />
          </div>
        </div>
        {!isDefault && (
          <button
            onClick={() => setParams(DEFAULT_PARAMS)}
            className="mt-4 text-[11px] text-gray-500 hover:text-gray-700 underline"
          >
            Reset to defaults
          </button>
        )}
      </div>
    </details>
  )
}

export default function OpportunitiesView({ parts, stats }) {
  const [expanded, setExpanded] = useState(null)
  const [exporting, setExporting] = useState(false)
  const [selectedPart, setSelectedPart] = useState(null) // { fileId, filename }
  const [showTutorial, setShowTutorial] = useState(false)
  const [params, setParams] = useState(DEFAULT_PARAMS)
  const [exitSupplier, setExitSupplier] = useState('')
  const [negoSupplier, setNegoSupplier] = useState('')
  const [divisionPreference, setDivisionPreference] = useState(null)
  const [hierarchyData, setHierarchyData] = useState(null)

  // Fetch division hierarchy on mount
  useEffect(() => {
    const token = sessionStorage.getItem('userToken')
    if (!token) return
    fetch(`${API_BASE}/ocr/spaces/hierarchy/`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data && data.site_to_division && Object.keys(data.site_to_division).length > 0) {
          setHierarchyData(data)
        } else {
          // Fallback: use known division mapping until spaces are migrated
          setHierarchyData({
            levels: ['BU', 'Division'],
            tree: { AIG: { CMD: ['Germering', 'Madison', 'San Jose', 'Sunnyvale', 'Vilnius'], MSD: ['Brno', 'Eindhoven', 'Fremont', 'Hillsboro', 'Richardson'], CAD: ['Franklin'] } },
            site_to_division: { Germering: 'CMD', Madison: 'CMD', 'San Jose': 'CMD', Sunnyvale: 'CMD', Vilnius: 'CMD', Brno: 'MSD', Eindhoven: 'MSD', Fremont: 'MSD', Hillsboro: 'MSD', Richardson: 'MSD', Franklin: 'CAD' },
          })
        }
      })
      .catch(() => {
        // Fallback on error
        setHierarchyData({
          levels: ['BU', 'Division'],
          tree: { AIG: { CMD: ['Germering', 'Madison', 'San Jose', 'Sunnyvale', 'Vilnius'], MSD: ['Brno', 'Eindhoven', 'Fremont', 'Hillsboro', 'Richardson'], CAD: ['Franklin'] } },
          site_to_division: { Germering: 'CMD', Madison: 'CMD', 'San Jose': 'CMD', Sunnyvale: 'CMD', Vilnius: 'CMD', Brno: 'MSD', Eindhoven: 'MSD', Fremont: 'MSD', Hillsboro: 'MSD', Richardson: 'MSD', Franklin: 'CAD' },
        })
      })
  }, [])

  const siteToDivision = hierarchyData?.site_to_division || null
  const divisions = useMemo(() => {
    if (!hierarchyData?.tree) return []
    // Extract division names from tree (level2 values)
    const divs = new Set()
    Object.values(hierarchyData.tree).forEach(buNode => {
      if (typeof buNode === 'object' && !Array.isArray(buNode)) {
        Object.keys(buNode).forEach(d => divs.add(d))
      }
    })
    return [...divs].sort()
  }, [hierarchyData])

  const enrichedParts = useMemo(() => computePricePositions(parts), [parts])

  // Compute opportunities with division preference
  const opportunities = useMemo(() => {
    const opps = computeOpportunities(enrichedParts, {
      ...params,
      divisionPreference,
      siteToDivision,
    })
    opps.forEach(o => { o._negotiationTarget = params.negotiationTarget })
    return opps
  }, [enrichedParts, params, divisionPreference, siteToDivision])

  // Baseline opportunities (no preference) for delta comparison in KPI bar
  const baselineOpportunities = useMemo(() => {
    if (!divisionPreference) return null
    const opps = computeOpportunities(enrichedParts, { ...params, siteToDivision })
    return opps
  }, [enrichedParts, params, siteToDivision, divisionPreference])

  const dedupResult = deduplicatedSavings(opportunities)
  const totalSavings = dedupResult.total
  const highAch = opportunities.filter(o => (o.achievability?.score || 0) >= 80)
  const highAchSavings = deduplicatedSavings(highAch).total
  const totalSpend = stats?.total_spend || 0

  // Phase 5: Null-baseline — shuffled-data noise floor
  const nullBaseline = useMemo(() => {
    if (!enrichedParts || enrichedParts.length < 5) return null
    return computeNullBaseline(enrichedParts, { ...params, divisionPreference, siteToDivision })
  }, [enrichedParts, params, divisionPreference, siteToDivision])

  // Signal = max(0, real − noise floor) per lever
  const noiseFloor = nullBaseline
    ? (nullBaseline.negotiation || 0) + (nullBaseline.part_family || 0)
    : 0

  // Separate quarantined opportunities for display in a collapsed section
  const activeOpportunities = opportunities.filter(o => !o.quarantined)
  const quarantinedOpportunities = opportunities.filter(o => o.quarantined)

  const uniquePNs = new Set()
  activeOpportunities.forEach(o => o.parts.forEach(p => uniquePNs.add(p.pn)))
  const totalExposed = enrichedParts.filter(p => uniquePNs.has(p.pn)).reduce((s, p) => s + (p.total_spend || 0), 0)

  const byType = {}
  activeOpportunities.forEach(o => {
    if (!byType[o.type]) byType[o.type] = { count: 0, savings: 0 }
    byType[o.type].count++
    byType[o.type].savings += o.estSavings
  })
  const rawTotal = activeOpportunities.reduce((s, o) => s + o.estSavings, 0)
  const donutSegments = Object.entries(byType).map(([type, d]) => ({
    type, savings: d.savings, pct: rawTotal > 0 ? d.savings / rawTotal * 100 : 0,
    label: OPP_TYPES[type]?.label || type, color: OPP_TYPES[type]?.color || '#94a3b8',
  }))

  const achHigh = activeOpportunities.filter(o => o.achievability?.color === 'green').length
  const achMed = activeOpportunities.filter(o => o.achievability?.color === 'amber').length
  const achLow = activeOpportunities.filter(o => o.achievability?.color === 'red').length
  const achExplore = activeOpportunities.filter(o => o.achievability?.color === 'gray').length
  const achTotal = activeOpportunities.length || 1

  // Supplier list for the manual exit planner (ranked by total spend)
  const supplierOptions = useMemo(() => {
    const totals = {}
    enrichedParts.forEach(p => (p.suppliers || []).forEach(s => {
      if (s.name && s.unit_cost > 0 && s.spend > 0) totals[s.name] = (totals[s.name] || 0) + s.spend
    }))
    return Object.entries(totals).sort((a, b) => b[1] - a[1]).map(([name, spend]) => ({ name, spend }))
  }, [enrichedParts])

  const manualExit = useMemo(() => {
    if (!exitSupplier) return null
    return computeSupplierExit(enrichedParts, exitSupplier, params)
  }, [exitSupplier, enrichedParts, params])

  // Phase 8b: Negotiation dossier
  const negoDossier = useMemo(() => {
    if (!negoSupplier) return null
    return computeSupplierNegotiation(enrichedParts, negoSupplier, params)
  }, [negoSupplier, enrichedParts, params])

  // Savings display uses the configured multiplier
  // Tier-aware savings display: applies capture rate by evidence tier
  const capRate = (tier) => tier === 'A' ? params.captureA : tier === 'C' ? params.captureC : params.captureB
  const fmtSavings = (raw, tier) => formatEur(raw * capRate(tier || 'B'))
  const fmtSavingsRaw = (raw) => formatEur(raw)

  const handleExport = async () => {
    setExporting(true)
    try { await exportOpportunitiesXlsx(opportunities, { parts: enrichedParts, stats, params }) } finally { setExporting(false) }
  }

  if (opportunities.length === 0) {
    return (
      <div className="text-center py-16 text-gray-400">
        <p className="text-lg mb-2">No opportunities detected</p>
        <p className="text-sm">Upload purchase history with pricing (and a Site column) to enable savings analysis</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <style>{STEP_STYLE}</style>

      <ConfigPanel params={params} setParams={setParams} parts={enrichedParts} />

      {/* Executive summary hero */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <div className="flex gap-8 flex-wrap">
          <div className="flex-1 min-w-[300px]">
            <h2 className="text-xl font-bold text-gray-800 mb-1">Savings Opportunities</h2>
            <p className="text-xs text-gray-500 mb-5 max-w-xl">
              Algorithmically identified from cross-site price comparison, benchmarking, supply risk, and sourcing structure
              across <strong>{stats?.total_parts || 0} parts</strong> and <strong>{formatEur(totalSpend)}</strong> total addressable spend.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <div className="text-lg font-bold text-green-600">{formatEur(
                  Math.max(0, (dedupResult.byTier?.A || 0) * params.captureA
                  + (dedupResult.byTier?.B || 0) * params.captureB
                  + (dedupResult.byTier?.C || 0) * params.captureC
                  - noiseFloor * params.captureB)
                )}</div>
                <div className="text-[10px] font-semibold text-gray-500 uppercase mt-1">Achievable Annual Savings</div>
                <div className="text-[10px] text-gray-400">{activeOpportunities.length} opportunities · capture-rate adjusted</div>
                {noiseFloor > 0 && (
                  <div className="text-[9px] text-gray-400 mt-1" title="Noise floor (shuffled-data baseline) — the amount of apparent savings that random price variation produces. Subtracted from headline to report signal only.">
                    Noise floor {formatEur(noiseFloor)} subtracted
                  </div>
                )}
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <div className="flex items-baseline gap-2">
                  <span className="text-lg font-bold text-green-700">{fmtSavings(dedupResult.byTier?.A || 0, 'A')}</span>
                  <span className="text-[9px] font-semibold text-green-600 bg-green-50 px-1.5 py-0.5 rounded">A · 100%</span>
                </div>
                <div className="text-[10px] font-semibold text-gray-500 uppercase mt-1">Observed</div>
                <div className="flex items-baseline gap-2 mt-1.5">
                  <span className="text-sm font-bold text-blue-600">{fmtSavings(dedupResult.byTier?.B || 0, 'B')}</span>
                  <span className="text-[9px] font-semibold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">B · 50%</span>
                </div>
                <div className="text-[10px] text-gray-400">Inferred (capture rate applied)</div>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <div className="flex items-baseline gap-2">
                  <span className="text-sm font-bold text-gray-500">{fmtSavings(dedupResult.byTier?.C || 0, 'C')}</span>
                  <span className="text-[9px] font-semibold text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">C · 20%</span>
                </div>
                <div className="text-[10px] font-semibold text-gray-500 uppercase mt-1">If-you-consolidate</div>
                <div className="text-[10px] text-gray-400">Assumptions — not summed with A+B</div>
              </div>
            </div>
          </div>

          <div className="w-72 flex-shrink-0">
            <div className="flex items-center gap-3">
              <Donut segments={donutSegments} count={opportunities.length} />
              <div className="flex-1 space-y-1">
                {[...donutSegments].sort((a, b) => b.savings - a.savings).map(seg => (
                  <div key={seg.type} className="flex items-center gap-1.5 text-[11px]">
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: seg.color }} />
                    <span className="text-gray-600 flex-1 truncate">{seg.label}</span>
                    <span className="font-medium text-gray-700">{formatEur(seg.savings)}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-4">
              <div className="text-[10px] text-gray-400 uppercase mb-1">Achievability distribution</div>
              <div className="flex h-2 rounded-full overflow-hidden bg-gray-100">
                <div className="bg-green-500" style={{ width: `${achHigh / achTotal * 100}%` }} />
                <div className="bg-amber-500" style={{ width: `${achMed / achTotal * 100}%` }} />
                <div className="bg-red-500" style={{ width: `${achLow / achTotal * 100}%` }} />
                <div className="bg-gray-400" style={{ width: `${achExplore / achTotal * 100}%` }} />
              </div>
              <div className="flex gap-3 mt-1 text-[10px]">
                <span className="text-green-600">High: {achHigh}</span>
                <span className="text-amber-600">Med: {achMed}</span>
                <span className="text-red-600">Low: {achLow}</span>
                {achExplore > 0 && <span className="text-gray-500">Explore: {achExplore}</span>}
              </div>
            </div>
          </div>
        </div>
      </div>

      <MethodologyPanel />

      {/* Division Preference KPI Bar */}
      {siteToDivision && divisions.length > 0 && (
        <DivisionKPIBar
          opportunities={opportunities}
          divisionPreference={divisionPreference}
          onPreferenceChange={setDivisionPreference}
          siteToDivision={siteToDivision}
          baselineOpportunities={baselineOpportunities}
          params={params}
        />
      )}

      {/* Supplier Exit Planner — pick any supplier and model where its book moves */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-100 flex-wrap">
          <span className="text-base">🚪</span>
          <h3 className="text-sm font-semibold text-gray-700">Supplier Exit Planner</h3>
          <span className="text-xs text-gray-400 flex-1 min-w-[200px]">Model exiting any supplier — where its parts redistribute across the existing base. Veto a receiver to see the next-best plan.</span>
          <select
            value={exitSupplier}
            onChange={e => setExitSupplier(e.target.value)}
            className="text-xs border border-gray-300 rounded-lg px-3 py-1.5 bg-white min-w-[260px]"
          >
            <option value="">Select a supplier to exit…</option>
            {supplierOptions.map(s => (
              <option key={s.name} value={s.name}>{s.name} — {formatEur(s.spend)}</option>
            ))}
          </select>
          {exitSupplier && (
            <button onClick={() => setExitSupplier('')} className="text-xs text-gray-400 hover:text-gray-600 underline">clear</button>
          )}
        </div>
        {manualExit && (
          <SupplierExitDetail opp={manualExit} onPartClick={setSelectedPart} enrichedParts={enrichedParts} params={params} />
        )}
      </div>

      {/* Phase 8b: Negotiation Helper */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-100 flex-wrap">
          <span className="text-base">🤝</span>
          <h3 className="text-sm font-semibold text-gray-700">Negotiation Helper</h3>
          <span className="text-xs text-gray-400 flex-1 min-w-[200px]">Build a data-backed negotiation dossier for any supplier — evidence ladder, ask amounts, BATNA.</span>
          <select
            value={negoSupplier}
            onChange={e => setNegoSupplier(e.target.value)}
            className="text-xs border border-gray-300 rounded-lg px-3 py-1.5 bg-white min-w-[260px]"
          >
            <option value="">Select a supplier to negotiate…</option>
            {supplierOptions.map(s => (
              <option key={s.name} value={s.name}>{s.name} — {formatEur(s.spend)}</option>
            ))}
          </select>
          {negoSupplier && (
            <button onClick={() => setNegoSupplier('')} className="text-xs text-gray-400 hover:text-gray-600 underline">clear</button>
          )}
        </div>
        {negoDossier && (
          <div className="px-5 py-4 space-y-4">
            {/* Ask Ladder */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                <div className="text-xs text-green-600 font-semibold uppercase">Opening</div>
                <div className="text-lg font-bold text-green-700">{formatEur(negoDossier.ask.opening)}</div>
                <div className="text-[10px] text-green-600">{negoDossier.overview.totalSpend > 0 ? (negoDossier.ask.opening / negoDossier.overview.totalSpend * 100).toFixed(1) : 0}% of book</div>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                <div className="text-xs text-blue-600 font-semibold uppercase">Target</div>
                <div className="text-lg font-bold text-blue-700">{formatEur(negoDossier.ask.target)}</div>
                <div className="text-[10px] text-blue-600">{negoDossier.overview.totalSpend > 0 ? (negoDossier.ask.target / negoDossier.overview.totalSpend * 100).toFixed(1) : 0}% of book</div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
                <div className="text-xs text-amber-600 font-semibold uppercase">Walk-away</div>
                <div className="text-lg font-bold text-amber-700">{formatEur(negoDossier.ask.walkaway)}</div>
                <div className="text-[10px] text-amber-600">{negoDossier.overview.totalSpend > 0 ? (negoDossier.ask.walkaway / negoDossier.overview.totalSpend * 100).toFixed(1) : 0}% of book</div>
              </div>
            </div>

            {/* Overview strip */}
            <div className="flex flex-wrap gap-4 text-xs text-gray-600">
              <span><strong>{negoDossier.overview.partCount}</strong> parts</span>
              <span><strong>{formatEur(negoDossier.overview.totalSpend)}</strong> total spend</span>
              <span><strong>{negoDossier.overview.familyCount}</strong> families</span>
              <span><strong>{(negoDossier.overview.shareOfCategory * 100).toFixed(1)}%</strong> of category</span>
              <span><strong>{negoDossier.overview.goodItems.length}</strong> competitively priced (protect)</span>
            </div>

            {/* Tier A — observed evidence */}
            {negoDossier.tierA.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-green-700 uppercase mb-1">Tier A — Same-part evidence ({negoDossier.tierA.length})</h4>
                <div className="max-h-40 overflow-y-auto">
                  <table className="w-full text-[11px]">
                    <tbody>
                      {negoDossier.tierA.slice(0, 8).map((item, i) => {
                        const part = enrichedParts.find(p => p.pn === item.pn)
                        return (
                        <tr key={i} className="border-b border-gray-50">
                          <td className="py-1"><button onClick={() => setSelectedPart({ fileId: part?.file_id || null, filename: part?.filename || item.pn })} className="text-[#1e5045] hover:underline cursor-pointer font-medium">{item.pn}</button></td>
                          <td className="py-1 text-gray-500 truncate max-w-[160px]">{item.desc || '—'}</td>
                          <td className="py-1 text-right font-mono">€{item.uc.toFixed(2)}</td>
                          <td className="py-1 text-right text-green-600 font-mono">€{item.bestAltUc.toFixed(2)}</td>
                          <td className="py-1 text-right text-gray-500">{item.bestAltName}</td>
                          <td className="py-1 text-right font-semibold text-green-700">{formatEur(item.saving)}</td>
                        </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Tier B — model-inferred */}
            {negoDossier.tierB.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-blue-700 uppercase mb-1">Tier B — Above should-cost ({negoDossier.tierB.length})</h4>
                <div className="max-h-32 overflow-y-auto">
                  <table className="w-full text-[11px]">
                    <tbody>
                      {negoDossier.tierB.slice(0, 6).map((item, i) => {
                        const part = enrichedParts.find(p => p.pn === item.pn)
                        return (
                        <tr key={i} className="border-b border-gray-50">
                          <td className="py-1"><button onClick={() => setSelectedPart({ fileId: part?.file_id || null, filename: part?.filename || item.pn })} className="text-[#1e5045] hover:underline cursor-pointer font-medium">{item.pn}</button></td>
                          <td className="py-1 text-gray-500 truncate max-w-[160px]">{item.desc || '—'}</td>
                          <td className="py-1 text-right font-mono">€{item.uc.toFixed(2)}</td>
                          <td className="py-1 text-right text-blue-600 font-mono">€{item.should_cost.toFixed(2)}</td>
                          <td className="py-1 text-right font-semibold text-blue-700">{formatEur(item.saving)}</td>
                        </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Carrot & BATNA */}
            <div className="grid grid-cols-2 gap-3">
              {negoDossier.tierC.bundle.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-[10px] font-semibold text-gray-600 uppercase mb-1">Carrot (consolidation bundle)</div>
                  <div className="text-sm font-bold text-gray-700">{negoDossier.tierC.bundle.length} parts • {formatEur(negoDossier.tierC.volume.consolidatedSpend)}</div>
                  <div className="text-[10px] text-gray-500">{negoDossier.tierC.volume.message}</div>
                </div>
              )}
              <div className="bg-red-50 rounded-lg p-3">
                <div className="text-[10px] font-semibold text-red-600 uppercase mb-1">BATNA (walk-away leverage)</div>
                <div className="text-sm font-bold text-red-700">{Math.round(negoDossier.tierC.batna.redistributablePct * 100)}% redistributable</div>
                <div className="text-[10px] text-red-600">Net floor: {formatEur(negoDossier.tierC.batna.netFloor)} across {negoDossier.tierC.batna.receiverCount} receivers</div>
              </div>
            </div>

            {/* Validate */}
            {negoDossier.validate.length > 0 && (
              <div className="text-[10px] text-amber-600">⚠️ {negoDossier.validate.length} items quarantined (>4× ratio) — verify before presenting.</div>
            )}
          </div>
        )}
      </div>

      {/* Opportunities table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-700">All Opportunities <span className="ml-1 text-xs text-gray-400">{opportunities.length}</span></h3>
          <div className="flex items-center gap-2">
            {/* Division preference pills */}
            {divisions.length > 0 && (
              <div className="flex items-center gap-1 mr-2">
                <span className="text-[10px] text-gray-400 font-medium mr-1">Optimize for:</span>
                <button
                  onClick={() => setDivisionPreference(null)}
                  className={`px-2.5 py-1 text-[10px] rounded-full border transition-all ${
                    !divisionPreference
                      ? 'bg-gray-100 border-gray-300 text-gray-700 font-semibold'
                      : 'border-gray-200 text-gray-400 hover:border-gray-300 hover:text-gray-500'
                  }`}
                >All Equal</button>
                {divisions.map(div => (
                  <button
                    key={div}
                    onClick={() => setDivisionPreference(divisionPreference === div ? null : div)}
                    className={`px-2.5 py-1 text-[10px] rounded-full border transition-all ${
                      divisionPreference === div
                        ? 'bg-emerald-50 border-emerald-300 text-emerald-700 font-semibold'
                        : 'border-gray-200 text-gray-400 hover:border-gray-300 hover:text-gray-500'
                    }`}
                  >{div}</button>
                ))}
              </div>
            )}
            <button onClick={() => setShowTutorial(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 text-gray-600 text-xs font-medium rounded-lg hover:bg-gray-50 transition-colors">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              How to use
            </button>
            <button onClick={handleExport} disabled={exporting}
              className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#1e5045] text-white text-xs font-medium rounded-lg hover:bg-[#0d5d57] transition-colors disabled:opacity-50">
              <FaDownload size={11} />{exporting ? 'Exporting…' : 'Export Excel'}
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] text-gray-400 uppercase border-b border-gray-100">
                <th className="px-4 py-2.5 font-medium">Opportunity</th>
                <th className="px-4 py-2.5 font-medium">Type</th>
                <th className="px-4 py-2.5 font-medium text-right">Parts</th>
                <th className="px-4 py-2.5 font-medium text-right">Spend Exposed</th>
                <th className="px-4 py-2.5 font-medium text-right">Potential Savings</th>
                <th className="px-4 py-2.5 font-medium">Achievability</th>
              </tr>
            </thead>
            <tbody>
              {activeOpportunities.map((o, idx) => {
                const t = OPP_TYPES[o.type]
                const isOpen = expanded === idx
                return (
                  <React.Fragment key={idx}>
                    <tr className={`border-b border-gray-100 cursor-pointer hover:bg-gray-50 ${isOpen ? 'bg-gray-50' : ''}`} onClick={() => setExpanded(isOpen ? null : idx)}>
                      <td className="px-4 py-3">
                        <div className="flex items-start gap-2">
                          <FaChevronRight size={10} className={`mt-1 text-gray-400 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                          <div>
                            <span className="text-gray-800 font-medium">{o.title}</span>
                            {(() => { const folders = [...new Set(o.parts.map(p => p.folder_name).filter(Boolean))]; return folders.length > 0 ? <div className="flex flex-wrap gap-1 mt-1">{folders.map(f => <span key={f} className="inline-block px-1.5 py-0.5 text-[9px] font-medium bg-gray-100 text-gray-500 rounded">{f}</span>)}</div> : null })()}
                            {/* Division affinity badges */}
                            {o.divisionBreakdown && Object.keys(o.divisionBreakdown).length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {Object.entries(o.divisionBreakdown).sort((a, b) => b[1] - a[1]).map(([div, spend]) => {
                                  const totalDivSpend = Object.values(o.divisionBreakdown).reduce((s, v) => s + v, 0)
                                  const pct = totalDivSpend > 0 ? Math.round((spend / totalDivSpend) * 100) : 0
                                  if (pct < 5) return null
                                  const isPreferred = div === divisionPreference
                                  return <span key={div} className={`inline-block px-1.5 py-0.5 text-[9px] font-medium rounded ${isPreferred ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-50 text-gray-400'}`}>{div} {pct}%</span>
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap" style={{ background: t.bg, color: t.color }}>{t.icon} {t.label}</span>
                        {o.evidenceTier && <span className={`ml-1 inline-block px-1.5 py-0.5 text-[9px] font-bold rounded ${o.evidenceTier === 'A' ? 'bg-green-50 text-green-700' : o.evidenceTier === 'B' ? 'bg-blue-50 text-blue-600' : 'bg-gray-100 text-gray-500'}`} title={o.evidenceTier === 'A' ? 'Observed same-part evidence' : o.evidenceTier === 'B' ? 'Model-inferred' : 'Assumption-based'}>{o.evidenceTier}</span>}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-600">{o.vizData?.partCount ?? o.parts.length}</td>
                      <td className="px-4 py-3 text-right font-mono text-gray-700">{formatEur(o.spendExposed)}</td>
                      <td className="px-4 py-3 text-right font-mono font-semibold text-green-600" title={`Raw: ${formatEur(o.estSavings)} × ${Math.round(capRate(o.evidenceTier) * 100)}% capture (Tier ${o.evidenceTier})`}>{fmtSavings(o.estSavings, o.evidenceTier)}</td>
                      <td className="px-4 py-3"><AchievabilityBadge achievability={o.achievability} /></td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td colSpan={6} className="p-0"><ExpandedDetail opp={o} onPartClick={setSelectedPart} enrichedParts={enrichedParts} params={params} /></td>
                      </tr>
                    )}
                  </React.Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Quarantined opportunities — collapsed section (Phase 1a) */}
      {quarantinedOpportunities.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-amber-200 mt-4">
          <button
            className="w-full flex items-center justify-between px-5 py-3 text-left"
            onClick={() => setExpanded(expanded === 'quarantine' ? null : 'quarantine')}
          >
            <span className="flex items-center gap-2 text-sm font-medium text-amber-700">
              <span className="inline-block w-2 h-2 rounded-full bg-amber-400" />
              Verify data first ({quarantinedOpportunities.length})
            </span>
            <FaChevronRight size={10} className={`text-amber-400 transition-transform ${expanded === 'quarantine' ? 'rotate-90' : ''}`} />
          </button>
          {expanded === 'quarantine' && (
            <div className="px-5 pb-4 border-t border-amber-100">
              <p className="text-xs text-amber-600 mt-3 mb-3">
                These items have price ratios that suggest data issues (unit-of-measure mismatches, currency errors, or pack-size differences).
                They are excluded from the savings headline until verified.
              </p>
              <table className="w-full text-sm">
                <tbody>
                  {quarantinedOpportunities.map((o, idx) => {
                    const t = OPP_TYPES[o.type]
                    return (
                      <tr key={idx} className="border-b border-amber-50">
                        <td className="px-2 py-2 text-gray-700">{o.title}</td>
                        <td className="px-2 py-2"><span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap" style={{ background: '#fef3c7', color: '#92400e' }}>{t?.icon} {t?.label || o.type}</span></td>
                        <td className="px-2 py-2 text-right text-amber-700 font-medium">{formatEur(o.estSavings)}</td>
                        <td className="px-2 py-2 text-xs text-amber-600 max-w-xs">{o.quarantineReason}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Part Detail Slide-Out */}
      {selectedPart && (
        <>
          <div className="fixed inset-0 bg-black/20 z-40" onClick={() => setSelectedPart(null)} />
          <AtlasPartDetailPanel
            fileId={selectedPart.fileId}
            filename={selectedPart.filename}
            onClose={() => setSelectedPart(null)}
            onNavigate={(newId) => setSelectedPart({ fileId: newId, filename: null })}
          />
        </>
      )}

      {/* Tutorial Overlay */}
      {showTutorial && <TutorialOverlay onClose={() => setShowTutorial(false)} />}
    </div>
  )
}
